import i18next from 'i18next'
import { dispatchInlineToolEvent } from '@/hooks/useServerEvents'
import { openCreditsPurchaseDialog } from '@/components/upgrade/credits-purchase-dialog'
import { createChatFiles } from '@/db/chat-files'
import { createChat, tagChatAsSidekick, touchChatActivity } from '@/db/chats'
import { createMessageFileItems } from '@/db/message-file-items'
import { createMessage, createMessages, deleteMessage, getMessagesByChatId, updateMessage } from '@/db/messages'
import { uploadMessageImage } from '@/db/storage/message-images'
import {
  ensureCrewBindingForChat,
  resolveCrewChatRosterForChatRequest,
  resolveCrewMemberForChatRequest
} from '@/lib/crew/crew-chat-binding'
import {
  isAwaitingShowPlanPayload,
  notifyCrewApprovalsRefresh
} from '@/lib/crew/crew-approval-cards-client'
import { buildCrewChatSystemPrompt } from '@/lib/crew/crew-chat'
import { enrichCrewChatSystemPromptWithDesignations } from '@/lib/crew/member-designation'
import { Tables, TablesInsert, Json } from '@/supabase/types'
import { ChatFile, ChatMessage, ChatPayload, ChatSettings, LLM, MessageImage } from '@/types'
import type { MessageMetadata } from '@/types/messages'
import { countTokens } from '@/lib/tokens/count-tokens'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import {
  buildClaudeFinalMessages,
  buildFinalMessages,
  buildGoogleGeminiFinalMessages,
  invalidatePromptMemoriesCache,
  warmPromptMemoriesCache
} from '@/lib/build-prompt'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { readChatApiErrorMessage } from '@/lib/chat/chat-api-error'
import { sanitizeAssistantVisibleContent } from '@/lib/chat/assistant-visible-content'
import { readFreeLosiMaxLimitDetails } from '@/lib/chat/free-losi-max-limit-notice'
import { isGroupChatMentionOnlyErrorMessage } from '@/lib/chat/group-chat-response-mode'
import { getNonBlobImageSource } from '@/lib/chat/message-image-source'
import {
  filterUnpersistedChatMessages,
  getMessagePersistenceSlot,
  mergePersistedChatMessages
} from '@/lib/chat/message-persistence-merge'
import { mergeAssistantMessageAnnotation } from '@/lib/chat/merge-assistant-message-annotation'
import { appendStreamTextDelta } from '@/lib/chat/resolve-output-text-done-delta'
import { closeOpenTextSegmentsAt } from '@/lib/chat/normalize-render-segments'
import { consumeReadableStream, parseDataStream } from '@/lib/consume-stream'
import { getCachedDesktopRuntimeConfig, getDesktopRuntimeConfig } from '@/lib/desktop-runtime'
import { getDesktopAccessTokenHeaders } from '@/lib/desktop-supabase-client'
import { isDesktopHost } from '@/lib/desktop-host'
import { SubscriptionRequiredError } from '@/lib/errors'
import {
  getImageServeStoragePath,
  isModelReadableImageUrl,
  normalizeMessageImageStorageSource,
  readUrlCandidate
} from '@/lib/media/safe-url'
import {
  parseCodeBlocksAndContent,
  parseChatMessageCodeBlocksAndContent,
  reconstructContentWithCodeBlocks,
  reconstructContentWithCodeBlocksInChatMessage
} from '@/lib/messages'
import { resolveOllamaApiUrl } from '@/lib/models/fetch-models'
import {
  getAndClearModelId,
  getAndClearProviderId,
  getAndClearSessionId,
  storeSessionData
} from '@/lib/session-storage'
import { validatePlanForModel, validatePlanForTools } from '@/lib/subscription'
import { useCodeBlockManager } from '@/hooks/useCodeBlockManager'
import { logger } from '@/lib/logger'
import { addChatAtom } from '@/atoms/workspaceAtoms'
import { safeConsole } from '@/lib/utils/safeConsole'
import { getMessageImageFromStorage } from '@/db/storage/message-images'
import {
  hasAssistantResponseControlMarker,
  mergeAssistantResponseControlMetadata,
  parseSuggestions
} from '@/lib/utils/parse-suggestions'
import { mergeLosiSteps } from '@/lib/chat/losi-steps-merge'
import { isTryTempFileId } from '@/lib/chat/try-temp-attachments'
import {
  buildPendingAssistantTurnMetadata,
  completePendingAssistantTurnMetadata,
  failPendingAssistantTurnMetadata
} from '@/lib/chat/pending-assistant-turn'
import { showFreeLosiMaxLimitToast } from '../free-losi-max-limit-toast'

const CONNECTED_EXTENSIONS_CACHE_TTL_MS = 5 * 60 * 1000
let cachedConnectedExtensionNames: { names: string[]; expiresAt: number } | null = null

function readCachedConnectedExtensionNames(): string[] {
  if (cachedConnectedExtensionNames && cachedConnectedExtensionNames.expiresAt > Date.now()) {
    return cachedConnectedExtensionNames.names
  }
  return []
}

async function refreshConnectedExtensionNames(): Promise<string[]> {
  try {
    const extensionsResponse = await fetchProtectedApi('/api/extensions?type=connected')
    if (!extensionsResponse.ok) {
      return readCachedConnectedExtensionNames()
    }

    const connectedExtensions = await extensionsResponse.json()
    const names = (Array.isArray(connectedExtensions) ? connectedExtensions : [])
      .filter((ext: { is_active?: boolean }) => ext?.is_active)
      .map((ext: { extension_name?: string }) => ext.extension_name)
      .filter((name: unknown): name is string => typeof name === 'string' && name.length > 0)

    cachedConnectedExtensionNames = {
      names,
      expiresAt: Date.now() + CONNECTED_EXTENSIONS_CACHE_TTL_MS
    }
    return names
  } catch (error) {
    safeConsole.warn(`[handleToolsChat] Error loading connected extensions:`, error)
    return readCachedConnectedExtensionNames()
  }
}

/**
 * Fire-and-forget warm for work that otherwise blocks tools-stream start.
 * Call while persisting the user/pending assistant rows.
 */
export function warmToolsChatSendCaches(options: {
  chatId?: string | null
  messageContent?: string | null
  userId?: string | null
}) {
  if (typeof window === 'undefined') {
    return
  }

  void refreshConnectedExtensionNames()

  const chatId =
    typeof options.chatId === 'string' && options.chatId.trim().length > 0
      ? options.chatId.trim()
      : null

  if (!chatId) {
    return
  }

  const query =
    typeof options.messageContent === 'string' && options.messageContent.trim().length > 0
      ? options.messageContent.trim().slice(0, 500)
      : null

  void warmPromptMemoriesCache({
    chatId,
    query,
    userId: options.userId
  })
}

// Helper function to translate that ensures string return type
const t = (key: string, options?: Record<string, any>): string => {
  const translation = i18next.t(key, options)
  return typeof translation === 'string' ? translation : key
}

const fetchProtectedApi = (input: RequestInfo | URL, init?: RequestInit) => {
  if (typeof input === 'string' && input.startsWith('/api/')) {
    return authenticatedFetch(input, init)
  }

  return fetch(input, init)
}

type PromptTool = NonNullable<Parameters<typeof buildFinalMessages>[3]>[number]

type ProcessResponseResult = {
  generatedText: string
  data: unknown
  sessionId: string | null
  modelId: string | null
  providerId: string | null
  /** Present when the model stopped early (e.g. token cap → `length`). */
  finishReason?: string | null
}

type ChatRequestMessage = {
  role?: unknown
  content?: unknown
  [key: string]: unknown
}

type ChatContextCompaction = {
  summary: string
  droppedMessageCount: number
}

type CompactMessagesResult = {
  messages: unknown[]
  compaction: ChatContextCompaction | null
}

const COMPACTED_CONTEXT_RECENT_MESSAGE_COUNT = 8
const COMPACTED_CONTEXT_EMERGENCY_MESSAGE_COUNT = 4
const COMPACTED_CONTEXT_MESSAGE_PREVIEW_CHARS = 420
const COMPACTED_CONTEXT_SUMMARY_CHARS = 2600

const clipCompactedText = (value: string, maxChars: number) =>
  value.length > maxChars ? `${value.slice(0, maxChars).trimEnd()}...` : value

const readRequestMessageRole = (message: ChatRequestMessage) =>
  typeof message.role === 'string' && message.role.trim()
    ? message.role.trim()
    : 'message'

const readRequestContentText = (content: unknown): string => {
  if (typeof content === 'string') {
    return content
  }

  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (!part || typeof part !== 'object' || Array.isArray(part)) {
          return ''
        }

        const record = part as Record<string, unknown>
        if (typeof record.text === 'string') {
          return record.text
        }

        if (record.type === 'image' || record.type === 'image_url') {
          return '[image]'
        }

        return ''
      })
      .filter(Boolean)
      .join('\n')
  }

  return ''
}

const buildCompactedContext = (
  droppedMessages: ChatRequestMessage[]
): { message: ChatRequestMessage; compaction: ChatContextCompaction } | null => {
  const summaryLines = droppedMessages
    .map((message, index) => {
      const text = readRequestContentText(message.content).replace(/\s+/g, ' ').trim()

      if (!text) {
        return null
      }

      return `${index + 1}. ${readRequestMessageRole(message)}: ${clipCompactedText(
        text,
        COMPACTED_CONTEXT_MESSAGE_PREVIEW_CHARS
      )}`
    })
    .filter((line): line is string => Boolean(line))

  if (summaryLines.length === 0) {
    return null
  }

  const summary = clipCompactedText(
    [
      'Earlier conversation was compacted automatically to keep this turn sendable.',
      'Use this continuity summary as background. Newer messages below are authoritative.',
      `Compacted ${droppedMessages.length} older message(s):`,
      ...summaryLines
    ].join('\n'),
    COMPACTED_CONTEXT_SUMMARY_CHARS
  )

  return {
    message: {
      role: 'system',
      content: summary
    },
    compaction: {
      summary,
      droppedMessageCount: droppedMessages.length
    }
  }
}

export const compactMessagesForPayload = (
  messages: unknown[],
  recentMessageCount: number
): CompactMessagesResult => {
  const typedMessages = messages.filter(
    (message): message is ChatRequestMessage =>
      typeof message === 'object' && message !== null && !Array.isArray(message)
  )

  if (typedMessages.length <= recentMessageCount + 1) {
    return { messages, compaction: null }
  }

  const [firstMessage, ...remainingMessages] = typedMessages
  const hasSystemMessage = readRequestMessageRole(firstMessage) === 'system'
  const systemMessages = hasSystemMessage ? [firstMessage] : []
  const conversationMessages = hasSystemMessage ? remainingMessages : typedMessages
  const droppedCount = Math.max(0, conversationMessages.length - recentMessageCount)

  if (droppedCount === 0) {
    return { messages, compaction: null }
  }

  const droppedMessages = conversationMessages.slice(0, droppedCount)
  const recentMessages = conversationMessages.slice(droppedCount)
  const compactedContext = buildCompactedContext(droppedMessages)

  return {
    messages: [
      ...systemMessages,
      ...(compactedContext ? [compactedContext.message] : []),
      ...recentMessages
    ],
    compaction: compactedContext?.compaction ?? null
  }
}

// A prior compaction persists its summary on the assistant message it was
// generated for (metadata.chatContextCompaction). Without anchoring future
// turns to that point, every subsequent send still starts from the full,
// ever-growing chatMessages array, is still oversized, and re-runs the same
// compaction from scratch on every turn - the "compacts every message"
// behavior this fixes. Once a compaction boundary exists, later turns only
// need the messages from that point forward; the persisted summary (already
// surfaced via buildPriorChatContextCompactionBlock) stands in for the rest,
// so compaction only needs to fire again once new growth earns it.
export function anchorChatMessagesToLatestCompaction(
  chatMessages: ChatMessage[]
): ChatMessage[] {
  for (let index = chatMessages.length - 1; index >= 0; index -= 1) {
    const metadata = chatMessages[index]?.message?.metadata
    const compaction =
      metadata && typeof metadata === 'object' && !Array.isArray(metadata)
        ? (metadata as Record<string, unknown>).chatContextCompaction
        : null

    if (compaction && typeof compaction === 'object' && !Array.isArray(compaction)) {
      return chatMessages.slice(index)
    }
  }

  return chatMessages
}

const getJsonRecord = (value: Json | null | undefined) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, Json>)
    : null

const buildChatContextCompactionMetadata = ({
  metadata,
  compaction,
  originalSize,
  optimizedSize
}: {
  metadata: Json | null | undefined
  compaction: ChatContextCompaction
  originalSize: number
  optimizedSize: number
}): Json => {
  const existing = getJsonRecord(metadata)
  const compactedAt = new Date().toISOString()

  return {
    ...(existing ?? {}),
    chatContextCompaction: {
      status: 'running',
      summary: compaction.summary,
      droppedMessageCount: compaction.droppedMessageCount,
      originalSize,
      optimizedSize,
      compactedAt
    }
  } as Json
}

const buildChatContextCompactionGenerationContext = ({
  generationContext,
  compactionMetadata
}: {
  generationContext: Json | null | undefined
  compactionMetadata: Json
}): Json => {
  const existing = getJsonRecord(generationContext)

  return {
    ...(existing ?? {}),
    chatContextCompaction: getJsonRecord(compactionMetadata)?.chatContextCompaction ?? null
  } as Json
}

const getSafeChatImageBase64 = (image: MessageImage) => {
  const source = getNonBlobImageSource(image.base64)

  return source?.startsWith('data:image') ? source : ''
}

const getSafeChatImageUrl = (image: MessageImage) =>
  getSafeChatImageBase64(image) ||
  getNonBlobImageSource(image.url) ||
  getNonBlobImageSource(image.path)

const getMessageImageUploadSource = (image: MessageImage) => {
  const candidates = [image.base64, image.url]

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue

    const trimmed = candidate.trim()
    if (!trimmed) continue

    if (/^(data:|blob:|https?:\/\/)/i.test(trimmed)) {
      return trimmed
    }
  }

  return null
}

const hasFreshMessageImageUploadSource = (image: MessageImage) => {
  if (image.file) {
    return true
  }

  return [image.base64, image.url].some(
    (candidate) => typeof candidate === 'string' && /^(data:|blob:)/i.test(candidate.trim())
  )
}

const inferImageMimeType = (source: string, fallback = 'image/png') => {
  const match = source.match(/^data:([^;,]+)[;,]/i)
  return match?.[1] || fallback
}

const MESSAGE_IMAGE_ATTACHMENT_TIMEOUT_MS = 30_000
const MESSAGE_IMAGE_SIGN_TIMEOUT_MS = 5_000
const MESSAGE_IMAGE_BUCKET_PREFIX = /^\/?message_images\//

// --------------

export const withMessageImageOperationTimeout = async <T,>(
  operation: Promise<T>,
  label: string,
  timeoutMs = MESSAGE_IMAGE_ATTACHMENT_TIMEOUT_MS
): Promise<T> => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  try {
    return await Promise.race([
      operation,
      new Promise<T>((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error(`${label} timed out. Please try again.`))
        }, timeoutMs)
      })
    ])
  } finally {
    if (timeout) {
      clearTimeout(timeout)
    }
  }
}

// --------------

const normalizeReusableMessageImageStoragePath = (value: unknown): string | null => {
  const candidate = readUrlCandidate(value)

  if (!candidate) {
    return null
  }

  const servedPath = getImageServeStoragePath(candidate)
  const source = normalizeMessageImageStorageSource(servedPath || candidate)

  if (!source) {
    return null
  }

  if (
    isModelReadableImageUrl(source) ||
    /^blob:/i.test(source) ||
    source.startsWith('/api/image/serve?') ||
    source.startsWith('api/image/serve?')
  ) {
    return null
  }

  const storagePath = source
    .replace(MESSAGE_IMAGE_BUCKET_PREFIX, '')
    .replace(/^\/+/, '')

  return storagePath.includes('/') && !storagePath.includes(' ')
    ? storagePath
    : null
}

// --------------

export const getReusableMessageImageStoragePath = (
  image: Pick<MessageImage, 'path' | 'url' | 'base64'>
) => {
  for (const candidate of [image.path, image.url, image.base64]) {
    const path = normalizeReusableMessageImageStoragePath(candidate)

    if (path) {
      return path
    }
  }

  return null
}

// --------------

export const resolveMessageImageUploadFile = async (
  image: MessageImage,
  fallbackName: string
): Promise<File | null> => {
  if (image.file) {
    return image.file
  }

  const source = getMessageImageUploadSource(image)
  if (!source) {
    return null
  }

  const response = await withMessageImageOperationTimeout(
    fetch(source),
    'Image attachment source load'
  )

  if (!response.ok) {
    throw new Error(`Failed to load image upload source: ${response.status}`)
  }

  const blob = await withMessageImageOperationTimeout(
    response.blob(),
    'Image attachment decode'
  )
  return new File([blob], fallbackName, {
    type: blob.type || inferImageMimeType(source)
  })
}

// --------------

type MessageImageUploadLimitContext = {
  uploadLimitChatId?: string | null
  uploadLimitSpaceId?: string | null
  workspaceId?: string | null
}

const getMessageImageUploadLimitContext = (
  chat: Tables<'chats'>
): MessageImageUploadLimitContext | undefined => {
  const canUseWorkspacePlan =
    chat.is_group_chat ||
    chat.space_id ||
    chat.type === 'space_conversation'

  if (!canUseWorkspacePlan) {
    return undefined
  }

  return {
    uploadLimitChatId: chat.is_group_chat ? chat.id : null,
    uploadLimitSpaceId: chat.space_id,
    workspaceId: chat.workspace_id
  }
}

export const uploadMessageImageWithTimeout = (
  path: string,
  image: File,
  uploadLimitContext?: MessageImageUploadLimitContext
) =>
  withMessageImageOperationTimeout(
    uploadMessageImage(path, image, uploadLimitContext),
    'Image upload'
  )

// --------------

export const getMessageImageFromStorageWithTimeout = (path: string) =>
  withMessageImageOperationTimeout(
    getMessageImageFromStorage(path),
    'Image preview',
    MESSAGE_IMAGE_SIGN_TIMEOUT_MS
  )

// --------------

export const prepareChatMessageImages = async ({
  images,
  userId,
  chatId,
  fileNamePrefix = 'message-image',
  uploadLimitContext
}: {
  images: MessageImage[]
  userId: string
  chatId: string
  fileNamePrefix?: string
  uploadLimitContext?: MessageImageUploadLimitContext
}) => {
  const imagePaths: string[] = []
  const preparedImages: MessageImage[] = []

  for (let index = 0; index < images.length; index += 1) {
    const image = { ...images[index] }
    const existingPath = hasFreshMessageImageUploadSource(image)
      ? null
      : getReusableMessageImageStoragePath(image)

    if (existingPath) {
      image.path = existingPath
      image.url = image.url || image.base64 || existingPath
      imagePaths.push(existingPath)
      preparedImages.push(image)
      continue
    }

    const uploadFile = await resolveMessageImageUploadFile(
      image,
      `${fileNamePrefix}-${index + 1}.png`
    )

    if (!uploadFile) {
      throw new Error('Image attachment is still preparing. Please try again.')
    }

    const storagePath = `${userId}/${chatId}/${uuidv4()}`
    const uploadedPath = await uploadMessageImageWithTimeout(
      storagePath,
      uploadFile,
      uploadLimitContext
    )

    image.path = uploadedPath || storagePath
    image.file = uploadFile
    image.url = image.url || image.base64 || image.path
    imagePaths.push(image.path)
    preparedImages.push(image)
  }

  return {
    imagePaths,
    images: preparedImages
  }
}

const isPersistedChatFile = (file: ChatFile) =>
  Boolean(
    file.id &&
      !file.id.startsWith('loading') &&
      !file.isTemporary &&
      !isTryTempFileId(file.id)
  )

function mergeStreamAnnotationData(current: unknown, next: unknown): Record<string, unknown> {
  const currentRecord =
    current && typeof current === 'object' && !Array.isArray(current)
      ? (current as Record<string, unknown>)
      : {}
  const nextRecord =
    next && typeof next === 'object' && !Array.isArray(next)
      ? (next as Record<string, unknown>)
      : {}

  const merged = {
    ...currentRecord,
    ...nextRecord
  }

  if ('losi_steps' in nextRecord) {
    merged.losi_steps = mergeLosiSteps(
      currentRecord.losi_steps,
      nextRecord.losi_steps
    )
  }

  return merged
}

export {
  mergeAssistantMessageAnnotation,
  resolveAssistantAnnotationForPersist
} from '@/lib/chat/merge-assistant-message-annotation'

const normalizePromptToolHeaders = (
  headers: Json
): Record<string, string> | null => {
  const parsedHeaders = typeof headers === 'string'
    ? (() => {
        try {
          const parsed: unknown = JSON.parse(headers)

          return parsed
        } catch {
          return null
        }
      })()
    : headers

  if (
    !parsedHeaders ||
    typeof parsedHeaders !== 'object' ||
    Array.isArray(parsedHeaders)
  ) {
    return null
  }

  const stringEntries = Object.entries(parsedHeaders).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  )

  return stringEntries.length > 0 ? Object.fromEntries(stringEntries) : null
}

const normalizePromptTool = (tool: Tables<'tools'>): PromptTool => {
  const toolRecord = tool as unknown as Record<string, unknown>

  return {
    id: tool.id,
    name: tool.name,
    enabled: typeof toolRecord.enabled === 'boolean' ? toolRecord.enabled : true,
    schema: tool.schema,
    custom_headers: normalizePromptToolHeaders(tool.custom_headers),
    description: tool.description
  }
}

const isInstalledIntegrationSelectedTool = (tool: Tables<'tools'>) =>
  tool.id.startsWith('mcp-integration:') || tool.id.startsWith('skill-integration:')

const getPromptToolsWithSelectedIntegrations = (
  allTools: Tables<'tools'>[],
  selectedTools: Tables<'tools'>[]
) => {
  const seenIds = new Set(allTools.map((tool) => tool.id))
  const selectedIntegrationTools = selectedTools.filter((tool) => {
    if (!isInstalledIntegrationSelectedTool(tool)) {
      return false
    }

    if (seenIds.has(tool.id)) {
      return false
    }

    seenIds.add(tool.id)
    return true
  })

  return [...allTools, ...selectedIntegrationTools]
}

function isAbortLikeError(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) {
    return true
  }

  if (!(error instanceof Error)) {
    return false
  }

  const normalizedMessage = error.message.toLowerCase()

  return (
    error.name === 'AbortError' ||
    normalizedMessage.includes('aborted') ||
    normalizedMessage.includes('abort')
  )
}

// !! DON' TAMPER

export const generateChatName = async (
  message: string,
  assistantMessage?: string
) => {
  try {
    const response = await fetchProtectedApi('/api/chat/name', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userMessage: message,
        ...(assistantMessage ? { assistantMessage } : {})
      })
    })
    if (!response.ok) throw new Error('Failed to generate chat name')
    const data = await response.json()
    const raw = data?.title || t('chat_helper.title_generator.default_title')
    return (raw as string).replace(/^(["'])(.*)\1$/, '$2')
  } catch (error) {
    safeConsole.error('Error generating chat name:', error)
    return t('chat_helper.title_generator.default_title')
  }
}

export const validateChatSettings = (
  chatSettings: ChatSettings | null,
  modelData: LLM | undefined,
  profile: Tables<'profiles'> | null,
  selectedWorkspace: Tables<'workspaces'> | null,
  messageContent: string,
  selectedAssistant: Tables<'assistants'> | null,
  selectedTools: Tables<'tools'>[],
  hasAttachments = false
) => {
  if (!chatSettings) {
    throw new Error(t('chat_helper.errors.chat_settings_missing'))
  }

  if (!modelData) {
    throw new Error(t('chat_helper.errors.model_missing'))
  }

  if (!messageContent && !hasAttachments) {
    throw new Error(t('chat_helper.errors.message_missing'))
  }

  if (!validatePlanForModel(profile, modelData.modelId)) {
    throw new SubscriptionRequiredError(t('chat_helper.errors.subscription_required'))
  }

  if (!validatePlanForTools(profile, selectedTools, modelData.modelId)) {
    const requiredPlan = modelData.tier === 'ultra' ? 'Ultra' : 'Pro'

    throw new SubscriptionRequiredError(
      t('chat_helper.errors.subscription_required_plan', { plan: requiredPlan })
    )
  }
}

export const handleRetrieval = async (
  userInput: string,
  newMessageFiles: ChatFile[],
  chatFiles: ChatFile[],
  embeddingsProvider: 'openai' | 'local',
  sourceCount: number
): Promise<Tables<'file_items'>[]> => {
  try {
    const validNewMessageFiles = newMessageFiles.filter(isPersistedChatFile)
    const validChatFiles = chatFiles.filter(isPersistedChatFile)
    const hasPendingFile = [...newMessageFiles, ...chatFiles].some((file) =>
      file.id.startsWith('loading')
    )

    let fileIds = [...validNewMessageFiles, ...validChatFiles].map((file) => file.id)

    if (fileIds.length === 0) {
      if (hasPendingFile) {
        safeConsole.log(t('chat_helper.file_processing.waiting'))
        return []
      }

      safeConsole.warn(t('chat_helper.file_processing.none_processed'))
      return []
    }

    const verifyResponse = await fetchProtectedApi('/api/retrieval/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fileIds })
    })

    const verifyData = await verifyResponse.json()

    if (!verifyResponse.ok) {
      if (
        verifyResponse.status === 202 &&
        verifyData.message === t('chat_helper.file_processing.in_progress')
      ) {
        safeConsole.log(t('chat_helper.file_processing.in_progress'), verifyData.processingFiles)
        await new Promise((resolve) => setTimeout(resolve, 2000))
        return handleRetrieval(
          userInput,
          newMessageFiles,
          chatFiles,
          embeddingsProvider,
          sourceCount
        )
      }

      if (verifyResponse.status === 422) {
        safeConsole.error(t('chat_helper.file_processing.some_failed'), verifyData.failedFiles)
        fileIds = fileIds.filter((id) => !verifyData.failedFiles.includes(id))
        if (fileIds.length === 0) {
          safeConsole.error(t('chat_helper.file_processing.none_processed'))
          return []
        }
      } else {
        throw new Error(
          `${t('chat_helper.file_processing.verification_failed')} ${verifyData.message}`
        )
      }
    }

    // Attachment-only composer sends may have empty text; retrieval still needs
    // a query string so file chunks are returned for the attached file IDs.
    const retrievalUserInput = userInput?.trim() || 'here'

    const response = await fetchProtectedApi('/api/retrieval/retrieve', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userInput: retrievalUserInput,
        fileIds,
        embeddingsProvider,
        sourceCount
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      safeConsole.error(
        t('chat_helper.file_processing.retrieval_error'),
        response.status,
        errorData
      )
      throw new Error(
        `${t('chat_helper.file_processing.retrieval_failed')} ${errorData.message || response.statusText}`
      )
    }

    const { results } = (await response.json()) as {
      results: Tables<'file_items'>[]
    }

    if (!results || results.length === 0) {
      safeConsole.warn(t('chat_helper.file_processing.no_results'))
      return []
    }

    safeConsole.log(t('chat_helper.file_processing.files_retrieved'), results.length, 'items')
    return results
  } catch (error) {
    safeConsole.error(t('chat_helper.file_processing.retrieval_error_detail'), error)
    return []
  }
}

export const createTempMessages = (
  messageContent: string,
  chatMessages: ChatMessage[],
  chatSettings: ChatSettings,
  b64Images: string[],
  isRegeneration: boolean,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  selectedAssistant: Tables<'assistants'> | null,
  userAnnotation: any = {},
  chatId: string | null = null,
  currentUserId: string | null = null,
  includeAssistantMessage = true,
  userMetadata: Json | null = null,
  assistantMetadata: Json | null = null
) => {
  const normalizedImagePaths = b64Images
    .map(imagePath => normalizeMessageImageStorageSource(imagePath))
    .filter((imagePath): imagePath is string => Boolean(imagePath))

  // Ensure proper sequence number by getting the max sequence_number from sorted messages
  const sortedMessages = [...chatMessages].sort(
    (a, b) => a.message.sequence_number - b.message.sequence_number
  )
  const nextSequenceNumber =
    sortedMessages.length > 0
      ? Math.max(...sortedMessages.map((msg) => msg.message.sequence_number)) + 1
      : 0

  let tempUserChatMessage: ChatMessage = {
    message: {
      chat_id: chatId ?? '',
      assistant_id: null,
      content: messageContent,
      created_at: '',
      id: uuidv4(),
      image_paths: normalizedImagePaths,
      model: chatSettings.model,
      role: 'user',
      sequence_number: nextSequenceNumber,
      updated_at: '',
      user_id: currentUserId ?? '',
      annotation: userAnnotation,
      word_count: 0,
      metadata: userMetadata as MessageMetadata | null,
      type: null
    },
    fileItems: [],
    renderKey: `render-user-${uuidv4()}`
  }

  let tempAssistantChatMessage: ChatMessage = {
    message: {
      chat_id: chatId ?? '',
      assistant_id: selectedAssistant?.id || null,
      content: '',
      created_at: '',
      id: uuidv4(),
      image_paths: [],
      model: chatSettings.model,
      role: 'assistant',
      sequence_number: nextSequenceNumber + 1,
      updated_at: '',
      user_id: '',
      annotation: {},
      word_count: 0,
      metadata: assistantMetadata as MessageMetadata | null,
      type: null
    },
    fileItems: [],
    renderKey: `render-assistant-${uuidv4()}`
  }

  let newMessages = []

  if (isRegeneration) {
    const lastMessageIndex = chatMessages.length - 1
    newMessages = chatMessages.map((chatMessage, index) => {
      if (index !== lastMessageIndex) {
        return chatMessage
      }

      return {
        ...chatMessage,
        message: {
          ...chatMessage.message,
          content: '',
          annotation: {},
          metadata: assistantMetadata != null
            ? (assistantMetadata as MessageMetadata)
            : chatMessage.message.metadata
        }
      }
    })
  } else {
    // Collapse a trailing duplicate user turn (same text) before appending.
    // Stop→restore→resend and edit races otherwise leave two identical bubbles.
    // Also collapse when a stop left user + empty pending assistant in-thread.
    const normalizedContent = messageContent.trim()
    let baseMessages = chatMessages
    if (!isRegeneration && normalizedContent) {
      let collapseIds = new Set<string>()
      const last = sortedMessages[sortedMessages.length - 1]
      const prev = sortedMessages[sortedMessages.length - 2]

      if (
        last?.message?.role === 'user' &&
        typeof last.message.content === 'string' &&
        last.message.content.trim() === normalizedContent
      ) {
        collapseIds.add(last.message.id)
      } else if (
        last?.message?.role === 'assistant' &&
        (!last.message.content || !String(last.message.content).trim()) &&
        prev?.message?.role === 'user' &&
        typeof prev.message.content === 'string' &&
        prev.message.content.trim() === normalizedContent
      ) {
        collapseIds.add(prev.message.id)
        collapseIds.add(last.message.id)
      }

      if (collapseIds.size > 0) {
        baseMessages = chatMessages.filter(
          chatMessage => !collapseIds.has(chatMessage.message.id)
        )
        const nextSeq =
          baseMessages.length > 0
            ? Math.max(
                ...baseMessages.map(msg => msg.message.sequence_number)
              ) + 1
            : 0
        tempUserChatMessage = {
          ...tempUserChatMessage,
          message: {
            ...tempUserChatMessage.message,
            sequence_number: nextSeq
          }
        }
        tempAssistantChatMessage = {
          ...tempAssistantChatMessage,
          message: {
            ...tempAssistantChatMessage.message,
            sequence_number: nextSeq + 1
          }
        }
      }
    }

    // Add new messages and sort by sequence_number to maintain proper order
    const pendingMessages = includeAssistantMessage
      ? [...baseMessages, tempUserChatMessage, tempAssistantChatMessage]
      : [...baseMessages, tempUserChatMessage]

    newMessages = pendingMessages.sort(
      (a, b) => a.message.sequence_number - b.message.sequence_number
    )
  }

  setChatMessages(newMessages.map(parseChatMessageCodeBlocksAndContent))

  return {
    tempUserChatMessage,
    tempAssistantChatMessage
  }
}

export const handleLocalChat = async (
  payload: ChatPayload,
  profile: Tables<'profiles'>,
  chatSettings: ChatSettings,
  tempAssistantMessage: ChatMessage,
  isRegeneration: boolean,
  newAbortController: AbortController,
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>,
  setFirstTokenReceived: React.Dispatch<React.SetStateAction<boolean>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setToolInUse: React.Dispatch<React.SetStateAction<string>>,
  onModelFallback?: (newModelId: string) => void
) => {
  const formattedMessages = await buildFinalMessages(payload, profile, [])
  const ollamaChatUrl = resolveOllamaApiUrl('/api/chat')

  if (!ollamaChatUrl) {
    throw new Error(t('models.errors.ollama_not_responding'))
  }

  // Ollama API: https://github.com/jmorganca/ollama/blob/main/docs/api.md
  const response = await fetchChatResponse(
    ollamaChatUrl,
    {
      model: chatSettings.model,
      messages: formattedMessages,
      options: {
        temperature: payload.chatSettings.temperature
      }
    },
    false,
    newAbortController,
    setIsGenerating,
    setChatMessages,
    undefined,
    3,
    1000,
    onModelFallback
  )

  return await processResponse(
    response,
    isRegeneration ? payload.chatMessages[payload.chatMessages.length - 1] : tempAssistantMessage,
    false,
    newAbortController,
    setFirstTokenReceived,
    setChatMessages,
    setToolInUse
  )
}

type ToolsChatRequestPayloadOptions = {
  payload: ChatPayload
  formattedMessages: unknown[]
  selectedTools: Tables<'tools'>[]
  isRegeneration: boolean
  isThinkDeeperEnabled?: boolean
  chatImages: MessageImage[]
}

export const buildToolsChatRequestPayload = ({
  payload,
  formattedMessages,
  selectedTools,
  isRegeneration,
  isThinkDeeperEnabled,
  chatImages
}: ToolsChatRequestPayloadOptions) => ({
  chatSettings: payload.chatSettings,
  messages: formattedMessages,
  selectedTools,
  tool_ids: selectedTools
    .map((tool) => (typeof tool?.id === 'string' ? tool.id.trim() : ''))
    .filter(Boolean),
  isRegeneration,
  isThinkDeeperEnabled,
  chatImages,
  isPublic: payload.isPublic || false,
  ...(payload.workspaceId ? { workspaceId: payload.workspaceId } : {}),
  ...(payload.addon ? { addon: payload.addon } : {}),
  ...(payload.sidecar ? { sidecar: payload.sidecar } : {}),
  ...(payload.structuredMentions
    ? { structuredMentions: payload.structuredMentions }
    : {}),
  ...(payload.chatId ? { chatId: payload.chatId } : {}),
  ...(payload.pendingAssistantMessageId
    ? { pendingAssistantMessageId: payload.pendingAssistantMessageId }
    : {}),
  ...(payload.crewMemberId ? { crewMemberId: payload.crewMemberId } : {}),
  ...(payload.crewRoster ? { crewRoster: payload.crewRoster } : {}),
  ...(payload.adjacent_pane_context
    ? { adjacent_pane_context: payload.adjacent_pane_context }
    : {}),
  ...(payload.linked_space_context
    ? { linked_space_context: payload.linked_space_context }
    : {}),
  ...(payload.source === 'sidekick' && payload.sidekick_context
    ? { source: 'sidekick' as const, sidekick_context: payload.sidekick_context }
    : {}),
  ...(payload.source === 'space' && payload.space_context
    ? { source: 'space' as const, space_context: payload.space_context }
    : {})
})

export type PreparedToolsChatRequest = {
  requestPayload: Record<string, unknown>
  usedTokens: number
  /** Present when payload optimization compacted history; apply after pending assistant id exists. */
  compactionMetadata: Json | null
  imagePathFingerprint: string
}

export const buildChatImagePathFingerprint = (images: MessageImage[]) =>
  images
    .map((image) => {
      if (image.path) return `path:${image.path}`
      if (image.base64) return `b64:${image.base64.length}`
      return `url:${image.url || ''}`
    })
    .sort()
    .join(';;')

export function patchPreparedToolsChatRequest(
  prepared: PreparedToolsChatRequest,
  payload: ChatPayload
): PreparedToolsChatRequest {
  const nextPayload: Record<string, unknown> = {
    ...prepared.requestPayload
  }

  if (payload.chatId) {
    nextPayload.chatId = payload.chatId
  } else {
    delete nextPayload.chatId
  }

  if (payload.pendingAssistantMessageId) {
    nextPayload.pendingAssistantMessageId = payload.pendingAssistantMessageId
  } else {
    delete nextPayload.pendingAssistantMessageId
  }

  if (payload.workspaceId) {
    nextPayload.workspaceId = payload.workspaceId
  }

  if (payload.addon) {
    nextPayload.addon = payload.addon
  }

  if (payload.sidecar) {
    nextPayload.sidecar = payload.sidecar
  }

  if (payload.structuredMentions) {
    nextPayload.structuredMentions = payload.structuredMentions
  }

  if (payload.adjacent_pane_context) {
    nextPayload.adjacent_pane_context = payload.adjacent_pane_context
  }

  if (payload.linked_space_context) {
    nextPayload.linked_space_context = payload.linked_space_context
  }

  if (payload.source === 'sidekick' && payload.sidekick_context) {
    nextPayload.source = 'sidekick'
    nextPayload.sidekick_context = payload.sidekick_context
  } else if (payload.source === 'space' && payload.space_context) {
    nextPayload.source = 'space'
    nextPayload.space_context = payload.space_context
  }

  return {
    ...prepared,
    requestPayload: nextPayload
  }
}

/**
 * Build the tools-stream wire payload (prompt + images + size optimize).
 * Safe to overlap with user/pending-assistant persist — call
 * patchPreparedToolsChatRequest before fetch once pendingAssistantMessageId exists.
 */
export async function prepareToolsChatRequest(options: {
  payload: ChatPayload
  profile: Tables<'profiles'>
  chatImages: MessageImage[]
  selectedTools: Tables<'tools'>[]
  isRegeneration: boolean
  isThinkDeeperEnabled?: boolean
  allTools?: Tables<'tools'>[]
  tempAssistantChatMessage: ChatMessage
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
}): Promise<PreparedToolsChatRequest> {
  const {
    payload,
    profile,
    chatImages,
    selectedTools,
    isRegeneration,
    isThinkDeeperEnabled,
    allTools,
    tempAssistantChatMessage,
    setChatMessages
  } = options

  const allAvailableTools = allTools ? [...allTools] : []

  if (allAvailableTools.length === 0 && typeof window !== 'undefined') {
    try {
      const cachedTools = localStorage.getItem('cached_platform_tools')
      if (cachedTools) {
        const platformTools = JSON.parse(cachedTools)
        if (Array.isArray(platformTools) && platformTools.length > 0) {
          safeConsole.log(
            `[prepareToolsChatRequest] Loaded ${platformTools.length} tools from localStorage cache`
          )
          allAvailableTools.push(...platformTools)
        }
      }
    } catch (error) {
      safeConsole.warn(`[prepareToolsChatRequest] Error loading tools from localStorage:`, error)
    }
  }

  safeConsole.log(
    `[prepareToolsChatRequest] Using ${selectedTools.length} selected tools from ${allAvailableTools.length} available tools`
  )

  // Connected extensions are prompt flavor only — never block tools-stream on a
  // cold /api/extensions round-trip. Use a short session cache; refresh in background.
  let connectedExtensionNames: string[] = readCachedConnectedExtensionNames()
  const extensionsRefresh = refreshConnectedExtensionNames().then((names) => {
    connectedExtensionNames = names
  })
  if (connectedExtensionNames.length === 0) {
    await Promise.race([
      extensionsRefresh,
      new Promise<void>((resolve) => setTimeout(resolve, 250))
    ])
  }

  const payloadWithTools: ChatPayload = {
    ...payload,
    chatMessages: anchorChatMessagesToLatestCompaction(payload.chatMessages),
    selectedTools
  }
  const promptTools = getPromptToolsWithSelectedIntegrations(
    allAvailableTools,
    selectedTools
  ).map(normalizePromptTool)

  const { finalMessages: formattedMessages, usedTokens } = await buildFinalMessages(
    payloadWithTools,
    profile!,
    chatImages,
    promptTools,
    connectedExtensionNames
  )

  const enhancedChatImages = await Promise.all(
    chatImages.map(async (img) => {
      if (img.path) {
        return {
          messageId: img.messageId,
          path: img.path,
          base64: '',
          url: getNonBlobImageSource(img.url) || '',
          isPersistent: true
        }
      }

      const safeBase64 = getSafeChatImageBase64(img)
      const safeUrl = getSafeChatImageUrl(img)

      return {
        messageId: img.messageId,
        path: img.path,
        base64: safeBase64,
        url: safeUrl,
        isPersistent: true
      }
    })
  )

  logger.debug(`Prepared ${enhancedChatImages.length} images for toolsChat`)

  const MAX_SAFE_PAYLOAD_SIZE = 3.5 * 1024 * 1024
  const CRITICAL_PAYLOAD_SIZE = 4 * 1024 * 1024
  const getPayloadSize = (value: unknown) => JSON.stringify(value).length
  const isPayloadTooLarge = (value: unknown) => getPayloadSize(value) > MAX_SAFE_PAYLOAD_SIZE
  const isPayloadCritical = (value: unknown) => getPayloadSize(value) > CRITICAL_PAYLOAD_SIZE
  let payloadCompaction: ChatContextCompaction | null = null

  const optimizePayload = async (wirePayload: Record<string, unknown>) => {
    let optimizedPayload = { ...wirePayload }
    const optimizationSteps: string[] = []
    let messagesBeforeHistoryCompaction = Array.isArray(optimizedPayload.messages)
      ? optimizedPayload.messages
      : []

    if (
      Array.isArray(optimizedPayload.chatImages) &&
      optimizedPayload.chatImages.length > 0
    ) {
      const latestUserMessageWithImage = findLatestUserMessageWithImage(formattedMessages)

      optimizedPayload.messages = (
        Array.isArray(optimizedPayload.messages) ? optimizedPayload.messages : []
      ).map((msg: any) => {
        if (
          msg.role === 'user' &&
          msg !== latestUserMessageWithImage &&
          Array.isArray(msg.content)
        ) {
          return {
            ...msg,
            content: msg.content.filter(
              (c: any) => c.type !== 'image' && c.type !== 'image_url'
            )
          }
        }
        return msg
      })

      optimizedPayload.chatImages = []
      messagesBeforeHistoryCompaction = Array.isArray(optimizedPayload.messages)
        ? optimizedPayload.messages
        : messagesBeforeHistoryCompaction
      optimizationSteps.push('Removed historical images')
    }

    if (isPayloadTooLarge(optimizedPayload) && Array.isArray(optimizedPayload.messages)) {
      const { messages: compressedMessages, compressedAny } = await compressMessageContentImages(
        optimizedPayload.messages,
        0.5,
        1000
      )

      if (compressedAny) {
        optimizedPayload.messages = compressedMessages
        messagesBeforeHistoryCompaction = compressedMessages
        optimizationSteps.push('Compressed remaining message images')
      }
    }

    if (isPayloadTooLarge(optimizedPayload) && Array.isArray(optimizedPayload.messages)) {
      const compacted = compactMessagesForPayload(
        optimizedPayload.messages,
        COMPACTED_CONTEXT_RECENT_MESSAGE_COUNT
      )
      optimizedPayload.messages = compacted.messages
      payloadCompaction = compacted.compaction ?? payloadCompaction
      optimizationSteps.push('Compacted older message history')
    }

    if (isPayloadCritical(optimizedPayload)) {
      const compacted = compactMessagesForPayload(
        messagesBeforeHistoryCompaction,
        COMPACTED_CONTEXT_EMERGENCY_MESSAGE_COUNT
      )
      optimizedPayload.messages = compacted.messages
      payloadCompaction = compacted.compaction ?? payloadCompaction

      if (isPayloadCritical(optimizedPayload) && Array.isArray(optimizedPayload.messages)) {
        const { messages: harderCompressedMessages, compressedAny } =
          await compressMessageContentImages(optimizedPayload.messages, 0.3, 800)

        if (compressedAny) {
          optimizedPayload.messages = harderCompressedMessages
          optimizationSteps.push('Compressed remaining message images further')
        }
      }

      const systemMessage = Array.isArray(optimizedPayload.messages)
        ? optimizedPayload.messages[0]
        : null

      if (
        systemMessage &&
        typeof systemMessage === 'object' &&
        !Array.isArray(systemMessage) &&
        typeof (systemMessage as { content?: unknown }).content === 'string' &&
        ((systemMessage as { content: string }).content.length > 5000)
      ) {
        const content = (systemMessage as { content: string }).content
        ;(systemMessage as { content: string }).content =
          content.substring(0, 2000) +
          '\n\n[System prompt truncated for payload optimization]\n\n' +
          content.substring(content.length - 1000)
        optimizationSteps.push('Truncated system prompt')
      }

      optimizationSteps.push('Emergency compacted message history')
    }

    return { optimizedPayload, optimizationSteps }
  }

  let requestPayload = buildToolsChatRequestPayload({
    payload,
    formattedMessages,
    selectedTools,
    isRegeneration,
    isThinkDeeperEnabled,
    chatImages: enhancedChatImages
  }) as Record<string, unknown>
  const originalSize = getPayloadSize(requestPayload)
  let compactionMetadata: Json | null = null

  if (isPayloadTooLarge(requestPayload)) {
    safeConsole.warn(
      `Payload too large: ${Math.round(originalSize / 1024)}KB, applying optimizations`
    )

    const { optimizedPayload, optimizationSteps } = await optimizePayload(requestPayload)
    requestPayload = optimizedPayload

    const optimizedSize = getPayloadSize(requestPayload)
    safeConsole.log(
      `Optimized payload: ${Math.round(optimizedSize / 1024)}KB (reduced by ${Math.round((originalSize - optimizedSize) / 1024)}KB)`
    )
    safeConsole.log('Optimization steps:', optimizationSteps.join(', '))

    if (payloadCompaction) {
      compactionMetadata = buildChatContextCompactionMetadata({
        metadata: tempAssistantChatMessage.message.metadata,
        compaction: payloadCompaction,
        originalSize,
        optimizedSize
      })

      toast.info('Compacting conversation context so this can send.', {
        id: `chat-context-compaction-${payload.chatId ?? tempAssistantChatMessage.message.id}`
      })
    }

    if (isPayloadCritical(requestPayload)) {
      throw new Error(
        `Message content too large even after optimization (${Math.round(optimizedSize / 1024)}KB). ` +
          'Please start a new chat or remove some content.'
      )
    }
  }

  // Apply compaction to local UI immediately; DB write waits until a real
  // pendingAssistantMessageId exists (see applyPreparedToolsChatCompaction).
  if (compactionMetadata) {
    tempAssistantChatMessage.message.metadata = compactionMetadata
    setChatMessages((previousMessages) =>
      previousMessages.map((chatMessage) =>
        chatMessage.message.id === tempAssistantChatMessage.message.id
          ? {
              ...chatMessage,
              message: {
                ...chatMessage.message,
                metadata: compactionMetadata
              }
            }
          : chatMessage
      )
    )
  }

  return {
    requestPayload,
    usedTokens,
    compactionMetadata,
    imagePathFingerprint: buildChatImagePathFingerprint(chatImages)
  }
}

export async function applyPreparedToolsChatCompaction(options: {
  prepared: PreparedToolsChatRequest
  assistantMessageId: string | null | undefined
  isPublic?: boolean
  generationContext?: unknown
}): Promise<void> {
  const { prepared, assistantMessageId, isPublic, generationContext } = options
  if (!prepared.compactionMetadata || !assistantMessageId || isPublic) {
    return
  }

  try {
    await updateMessage(assistantMessageId, {
      metadata: prepared.compactionMetadata,
      generation_context: buildChatContextCompactionGenerationContext({
        generationContext: generationContext ?? null,
        compactionMetadata: prepared.compactionMetadata
      })
    })
  } catch (error) {
    safeConsole.warn('Failed to persist chat context compaction metadata', error)
  }
}

export const handleToolsChat = async (
  payload: ChatPayload,
  profile: Tables<'profiles'>,
  tempAssistantChatMessage: ChatMessage,
  isRegeneration: boolean,
  newAbortController: AbortController,
  chatImages: MessageImage[],
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>,
  setFirstTokenReceived: React.Dispatch<React.SetStateAction<boolean>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setToolInUse: React.Dispatch<React.SetStateAction<string>>,
  selectedTools: Tables<'tools'>[],
  supportsStreaming = false,
  setResponseTimeToFirstToken?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTimeTotal?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTokensTotal?: React.Dispatch<React.SetStateAction<number>>,
  setRequestTokensTotal?: React.Dispatch<React.SetStateAction<number>>,
  isThinkDeeperEnabled?: boolean,
  allTools?: Tables<'tools'>[],
  onModelFallback?: (newModelId: string) => void,
  onSidekickToolActive?: (active: SidekickToolActivePayload) => void,
  preparedRequest?: PreparedToolsChatRequest | null
) => {
  const startTime = new Date().getTime()

  const prepared =
    preparedRequest ??
    (await prepareToolsChatRequest({
      payload,
      profile,
      chatImages,
      selectedTools,
      isRegeneration,
      isThinkDeeperEnabled,
      allTools,
      tempAssistantChatMessage,
      setChatMessages
    }))

  const requestPayload = patchPreparedToolsChatRequest(prepared, payload).requestPayload
  setRequestTokensTotal?.(prepared.usedTokens)

  await applyPreparedToolsChatCompaction({
    prepared,
    assistantMessageId:
      payload.pendingAssistantMessageId || tempAssistantChatMessage.message.id,
    isPublic: payload.isPublic,
    generationContext:
      'generation_context' in tempAssistantChatMessage.message
        ? tempAssistantChatMessage.message.generation_context
        : null
  })

  // Keep local assistant metadata in sync when prepare ran against a temp id
  // and persist later replaced the message object.
  if (prepared.compactionMetadata) {
    tempAssistantChatMessage.message.metadata = prepared.compactionMetadata
    setChatMessages((previousMessages) =>
      previousMessages.map((chatMessage) => {
        const matchesTemp = chatMessage.message.id === tempAssistantChatMessage.message.id
        const matchesPending =
          Boolean(payload.pendingAssistantMessageId) &&
          chatMessage.message.id === payload.pendingAssistantMessageId
        if (!matchesTemp && !matchesPending) {
          return chatMessage
        }
        return {
          ...chatMessage,
          message: {
            ...chatMessage.message,
            metadata: prepared.compactionMetadata
          }
        }
      })
    )
  }

  const response = await fetchChatResponse(
    supportsStreaming ? '/api/chat/tools-stream' : '/api/chat/tools',
    requestPayload,
    true,
    newAbortController,
    setIsGenerating,
    setChatMessages,
    undefined,
    3,
    1000,
    onModelFallback
  )

  setToolInUse('none')
  let result = await processResponse(
    response,
    isRegeneration
      ? payload.chatMessages[payload.chatMessages.length - 1]
      : tempAssistantChatMessage,
    true,
    newAbortController,
    setFirstTokenReceived,
    setChatMessages,
    setToolInUse,
    selectedTools,
    setResponseTimeToFirstToken,
    setResponseTimeTotal,
    setResponseTokensTotal,
    startTime,
    onSidekickToolActive
  )

  // Model hit the output token cap — keep generating until complete (or cap).
  let lengthContinueDepth = 0
  while (
    result.finishReason === 'length' &&
    lengthContinueDepth < MAX_GENERATION_LENGTH_CONTINUATIONS &&
    !newAbortController.signal.aborted
  ) {
    lengthContinueDepth += 1
    const priorMessages = Array.isArray(requestPayload.messages)
      ? (requestPayload.messages as unknown[])
      : []
    const continuePayload = {
      ...requestPayload,
      messages: [
        ...priorMessages,
        { role: 'assistant', content: result.generatedText },
        { role: 'user', content: GENERATION_CONTINUE_USER_PROMPT }
      ],
      continue_generation: true
    }

    const continueResponse = await fetchChatResponse(
      supportsStreaming ? '/api/chat/tools-stream' : '/api/chat/tools',
      continuePayload,
      true,
      newAbortController,
      setIsGenerating,
      setChatMessages,
      undefined,
      3,
      1000,
      onModelFallback
    )

    result = await processResponse(
      continueResponse,
      isRegeneration
        ? payload.chatMessages[payload.chatMessages.length - 1]
        : tempAssistantChatMessage,
      true,
      newAbortController,
      setFirstTokenReceived,
      setChatMessages,
      setToolInUse,
      selectedTools,
      setResponseTimeToFirstToken,
      setResponseTimeTotal,
      setResponseTokensTotal,
      startTime,
      onSidekickToolActive,
      {
        seedText: result.generatedText,
        seedData: result.data,
        depth: lengthContinueDepth
      }
    )
  }

  return result
}

// Function to find the latest user message with image
function findLatestUserMessageWithImage(messages: any[]) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (msg.role === 'user' && Array.isArray(msg.content)) {
      const hasImage = msg.content.some((c: any) => c.type === 'image' || c.type === 'image_url')
      if (hasImage) {
        return msg
      }
    }
  }
  return null
}

// Compresses base64 image content already embedded in message content parts
// (Claude's {type:'image', source:{data}} and OpenAI's {type:'image_url'}
// shapes). buildFinalMessages inlines chatImages' raw base64 directly into
// these parts, so compressing the separate chatImages array alone does not
// shrink the actual request payload - the same uncompressed bytes are still
// sitting in messages[].content.
export async function compressMessageContentImages(
  messages: unknown[],
  quality: number,
  maxWidth: number
): Promise<{ messages: unknown[]; compressedAny: boolean }> {
  let compressedAny = false

  const nextMessages = await Promise.all(
    messages.map(async (rawMessage) => {
      const message = rawMessage as { content?: unknown }
      if (!Array.isArray(message.content)) return rawMessage

      const nextContent = await Promise.all(
        message.content.map(async (rawPart) => {
          const part = rawPart as Record<string, any>

          if (
            part?.type === 'image' &&
            part.source?.type === 'base64' &&
            typeof part.source.data === 'string' &&
            part.source.data.length > 100_000
          ) {
            try {
              const mediaType =
                typeof part.source.media_type === 'string' ? part.source.media_type : 'image/jpeg'
              const compressedDataUrl = await compressImage(
                `data:${mediaType};base64,${part.source.data}`,
                quality,
                maxWidth
              )
              const compressedData = compressedDataUrl.split(',')[1]

              if (compressedData && compressedData.length < part.source.data.length) {
                compressedAny = true
                return {
                  ...part,
                  source: {
                    ...part.source,
                    media_type: 'image/jpeg',
                    data: compressedData
                  }
                }
              }
            } catch (error) {
              logger.debug(
                { error: String(error) },
                'Failed to compress Claude image content for payload size'
              )
            }
          }

          if (
            part?.type === 'image_url' &&
            typeof part.image_url?.url === 'string' &&
            part.image_url.url.startsWith('data:') &&
            part.image_url.url.length > 100_000
          ) {
            try {
              const compressed = await compressImage(part.image_url.url, quality, maxWidth)

              if (compressed.length < part.image_url.url.length) {
                compressedAny = true
                return {
                  ...part,
                  image_url: {
                    ...part.image_url,
                    url: compressed
                  }
                }
              }
            } catch (error) {
              logger.debug(
                { error: String(error) },
                'Failed to compress image_url content for payload size'
              )
            }
          }

          return part
        })
      )

      return { ...message, content: nextContent }
    })
  )

  return { messages: nextMessages, compressedAny }
}

// Multi-pass client-side image compression targeting a max byte size.
// Phone cameras (12-48MP) can produce 8-15MB originals; a single pass at
// 70% / 1200px may still yield 2-3MB, blowing past Vercel's 4.5MB body
// limit when multiple images are attached. This iterates quality and
// dimension reductions until the result fits under targetBytes.
const DEFAULT_IMAGE_TARGET_BYTES = 1 * 1024 * 1024 // 1MB per image

async function compressImage(
  base64: string,
  quality = 0.7,
  maxWidth = 1200,
  targetBytes = DEFAULT_IMAGE_TARGET_BYTES
): Promise<string> {
  // Load image once
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.crossOrigin = 'anonymous'
    i.onload = () => resolve(i)
    i.onerror = () => reject(new Error('Failed to load image for compression'))
    i.src = base64
  })

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context')

  // Progressive compression: reduce maxWidth and quality each pass
  let currentMaxWidth = maxWidth
  let currentQuality = quality
  const maxAttempts = 5

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let width = img.width
    let height = img.height

    if (width > currentMaxWidth) {
      height = (height * currentMaxWidth) / width
      width = currentMaxWidth
    }

    canvas.width = width
    canvas.height = height
    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(img, 0, 0, width, height)

    const result = canvas.toDataURL('image/jpeg', currentQuality)

    // Estimate byte size from base64 length (subtract data URI prefix)
    const base64Part = result.split(',')[1] || ''
    const byteSize = Math.ceil((base64Part.length * 3) / 4)

    if (byteSize <= targetBytes) {
      return result
    }

    // Reduce for next pass
    currentQuality = Math.max(0.25, currentQuality - 0.15)
    currentMaxWidth = Math.max(600, Math.round(currentMaxWidth * 0.7))
  }

  // Final aggressive attempt: 600px, 25% quality
  canvas.width = Math.min(600, img.width)
  canvas.height = Math.round(
    img.height * (canvas.width / img.width)
  )
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL('image/jpeg', 0.25)
}

export const handleHostedChat = async (
  payload: ChatPayload,
  profile: Tables<'profiles'>,
  modelData: LLM,
  tempAssistantChatMessage: ChatMessage,
  isRegeneration: boolean,
  newAbortController: AbortController,
  newMessageImages: MessageImage[],
  chatImages: MessageImage[],
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>,
  setFirstTokenReceived: React.Dispatch<React.SetStateAction<boolean>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setToolInUse: React.Dispatch<React.SetStateAction<string>>,
  setResponseTimeToFirstToken?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTimeTotal?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTokensTotal?: React.Dispatch<React.SetStateAction<number>>,
  setRequestTokensTotal?: React.Dispatch<React.SetStateAction<number>>,
  isAdvancedVoiceMode?: boolean,
  onModelFallback?: (newModelId: string) => void
) => {
  const provider =
    modelData.provider === 'openai' && profile.use_azure_openai ? 'azure' : modelData.provider

  let formattedMessages = []
  let usedTokens = 0

  if (provider === 'google') {
    ;({ finalMessages: formattedMessages, usedTokens } = await buildGoogleGeminiFinalMessages(
      payload,
      profile,
      newMessageImages
    ))
  } else if (provider === 'anthropic') {
    ;({ finalMessages: formattedMessages, usedTokens } = await buildClaudeFinalMessages(
      payload,
      profile,
      chatImages
    ))
  } else {
    ;({ finalMessages: formattedMessages, usedTokens } = await buildFinalMessages(
      payload,
      profile,
      chatImages
    ))
  }

  if (isAdvancedVoiceMode) {
    // Use Realtime API
    const realtimeResponse = await fetchProtectedApi('/api/realtime-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: formattedMessages.slice(-2), // Only send the last two messages
        model: modelData.modelId,
        temperature: payload.chatSettings.temperature
      })
    })

    if (!realtimeResponse.ok) {
      throw new Error(t('Failed to get response from Realtime API'))
    }

    const reader = realtimeResponse.body?.getReader()
    const decoder = new TextDecoder()

    while (true) {
      const { done, value } = await reader!.read()
      if (done) break
      const chunk = decoder.decode(value)

      setFirstTokenReceived(true)
      setChatMessages((prevMessages) => {
        const updatedMessages = [...prevMessages]
        const lastMessage = updatedMessages[updatedMessages.length - 1]
        lastMessage.message.content += chunk
        return updatedMessages.sort((a, b) => a.message.sequence_number - b.message.sequence_number)
      })
    }
  } else {
    // Existing code for non-realtime chat
    // ...
  }

  const apiEndpoint = provider === 'custom' ? '/api/chat/custom' : `/api/chat/${provider}`

  const requestBody = {
    chatSettings: {
      ...payload.chatSettings,
      model: modelData.hostedId || modelData.modelId
    },
    messages: formattedMessages,
    customModelId: provider === 'custom' ? modelData.hostedId : '',
    ...(payload.chatId ? { chatId: payload.chatId } : {}),
    ...(payload.structuredMentions
      ? { structuredMentions: payload.structuredMentions }
      : {}),
    ...(isRegeneration ? { isRegeneration: true } : {})
  }

  setRequestTokensTotal?.(usedTokens)

  const startTime = Date.now()

  const response = await fetchChatResponse(
    apiEndpoint,
    requestBody,
    true,
    newAbortController,
    setIsGenerating,
    setChatMessages,
    undefined,
    3,
    1000,
    onModelFallback
  )

  return await processResponse(
    response,
    isRegeneration
      ? payload.chatMessages[payload.chatMessages.length - 1]
      : tempAssistantChatMessage,
    true,
    newAbortController,
    setFirstTokenReceived,
    setChatMessages,
    setToolInUse,
    [],
    setResponseTimeToFirstToken,
    setResponseTimeTotal,
    setResponseTokensTotal,
    startTime
  )
}

// Thrown for a non-OK HTTP response so fetchWithRetry does not retry it.
class ChatResponseError extends Error {}

export const fetchChatResponse = async (
  url: string,
  body: object,
  isHosted: boolean,
  controller: AbortController,
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setPaywallOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  maxRetries = 3,
  retryDelay = 1000,
  onModelFallback?: (newModelId: string) => void
) => {
  let errorMessage = t('chat_helper.errors.fetch_response')
  let errorLogLevel = toast.error

  const readRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null

  const readPaygOverageDetails = (value: unknown) => {
    const payload = readRecord(value)
    const subscription = readRecord(payload?.subscription)
    if (subscription?.reason !== 'payg_credits_required') return null

    return {
      creditsRequired:
        typeof subscription.creditsRequired === 'number'
          ? subscription.creditsRequired
          : null,
      paymentPath:
        typeof subscription.paymentPath === 'string' && subscription.paymentPath.trim()
          ? subscription.paymentPath
          : '/api-credits'
    }
  }

  const showPaygOverageToast = (
    details: ReturnType<typeof readPaygOverageDetails>,
    message: string
  ) => {
    if (!details) return

    toast.warning('Included messages used', {
      description:
        details.creditsRequired && details.creditsRequired > 0
          ? `${message} This message needs ${details.creditsRequired} PAYG credit.`
          : message,
      action: {
        label: 'Buy credits',
        onClick: () => {
          // Open the in-chat purchase modal instead of leaving chat
          // for the standalone credits page.
          openCreditsPurchaseDialog()
        }
      },
      duration: 10000
    })
  }

  const fetchWithRetry = async (retriesLeft: number): Promise<Response> => {
    try {
      const desktopHost = isDesktopHost()
      const cachedDesktopRuntime = desktopHost ? getCachedDesktopRuntimeConfig() : null
      const desktopRuntime =
        desktopHost && !cachedDesktopRuntime?.desktopDeviceId
          ? await getDesktopRuntimeConfig({
              backgroundRefresh: false,
              allowStale: true,
              includeFallback: true,
              forceRefresh: true
            })
          : cachedDesktopRuntime
      const desktopDeviceId =
        typeof desktopRuntime?.desktopDeviceId === 'string'
          ? desktopRuntime.desktopDeviceId.trim()
          : ''
      const desktopAccessHeaders = desktopHost ? await getDesktopAccessTokenHeaders() : {}
      const bodyWithDesktop =
        desktopDeviceId && body && typeof body === 'object' && !Array.isArray(body)
          ? {
              ...body,
              desktopDeviceId:
                typeof (body as { desktopDeviceId?: unknown }).desktopDeviceId === 'string' &&
                (body as { desktopDeviceId?: string }).desktopDeviceId?.trim()
                  ? (body as { desktopDeviceId?: string }).desktopDeviceId?.trim()
                  : desktopDeviceId
            }
          : body
      // Crew chats: attach bound member + multi-mate roster so the server
      // scopes computer/vault and injects Crew Chat coordination prompt.
      // Prefer explicit body fields (ChatSendOptions) over sessionStorage so
      // Approvals / show_plan→awaiting still work when binding races.
      const requestChatId =
        bodyWithDesktop && typeof bodyWithDesktop === 'object' && !Array.isArray(bodyWithDesktop)
          ? ((bodyWithDesktop as { chatId?: unknown }).chatId as string | undefined)
          : undefined
      const bodyCrew =
        bodyWithDesktop && typeof bodyWithDesktop === 'object' && !Array.isArray(bodyWithDesktop)
          ? (bodyWithDesktop as {
              crewMemberId?: unknown
              crewRoster?: unknown
              crewComputerMode?: unknown
              crewChatSystemPrompt?: unknown
            })
          : null
      const bodyCrewMemberId =
        typeof bodyCrew?.crewMemberId === 'string' && bodyCrew.crewMemberId.trim()
          ? bodyCrew.crewMemberId.trim()
          : null
      const resolvedMemberId =
        bodyCrewMemberId || resolveCrewMemberForChatRequest(requestChatId)
      const resolvedRoster =
        (bodyCrew?.crewRoster && typeof bodyCrew.crewRoster === 'object'
          ? (bodyCrew.crewRoster as NonNullable<
              ReturnType<typeof resolveCrewChatRosterForChatRequest>
            >)
          : null) || resolveCrewChatRosterForChatRequest(requestChatId)
      ensureCrewBindingForChat({
        chatId: requestChatId,
        crewMemberId: resolvedMemberId,
        roster: resolvedRoster
      })
      const crewExtras: Record<string, unknown> = {}
      if (resolvedMemberId) crewExtras.crewMemberId = resolvedMemberId
      if (resolvedRoster) {
        crewExtras.crewRoster = resolvedRoster
        crewExtras.crewComputerMode =
          bodyCrew?.crewComputerMode || resolvedRoster.computerMode
        crewExtras.crewChatSystemPrompt =
          (typeof bodyCrew?.crewChatSystemPrompt === 'string' &&
          bodyCrew.crewChatSystemPrompt.trim()
            ? bodyCrew.crewChatSystemPrompt.trim()
            : null) ||
          enrichCrewChatSystemPromptWithDesignations(
            buildCrewChatSystemPrompt(resolvedRoster),
            resolvedRoster
          )
      } else if (
        typeof bodyCrew?.crewChatSystemPrompt === 'string' &&
        bodyCrew.crewChatSystemPrompt.trim()
      ) {
        crewExtras.crewChatSystemPrompt = bodyCrew.crewChatSystemPrompt.trim()
        if (bodyCrew.crewComputerMode) {
          crewExtras.crewComputerMode = bodyCrew.crewComputerMode
        }
      }
      const requestBody =
        Object.keys(crewExtras).length > 0 &&
        bodyWithDesktop &&
        typeof bodyWithDesktop === 'object' &&
        !Array.isArray(bodyWithDesktop)
          ? { ...(bodyWithDesktop as Record<string, unknown>), ...crewExtras }
          : bodyWithDesktop

      // Log request size
      const bodyStr = JSON.stringify(requestBody)
      const bodySizeKB = Math.round(bodyStr.length / 1024)
      safeConsole.log(`Request size: ${bodySizeKB} KB for ${url}`)

      const response = await fetchProtectedApi(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...desktopAccessHeaders,
          ...(desktopHost ? { 'expo-platform': 'electron' } : {}),
          ...(desktopDeviceId ? { 'x-losi-desktop-device-id': desktopDeviceId } : {})
        },
        body: bodyStr,
        signal: controller.signal
      })
      if (!response.ok) {
        let errorData: unknown = null
        try {
          errorData = await response.clone().json()
        } catch {
          errorData = null
        }

        const freePlanTokenLimitExceeded =
          response.status === 403 &&
          readRecord(errorData)?.error === 'FREE_PLAN_TOKEN_LIMIT_EXCEEDED'

        if (freePlanTokenLimitExceeded) {
          errorMessage = readChatApiErrorMessage(
            errorData,
            t('chat_helper.errors.send_message')
          )
          setPaywallOpen?.(true)
          throw new SubscriptionRequiredError(errorMessage)
        }

        safeConsole.error(t('chat_helper.errors.fetch_response_detail'), response)

        // Enhanced handling for 413 errors
        if (response.status === 413) {
          const requestSize = Math.round((bodyStr.length / 1024 / 1024) * 100) / 100
          safeConsole.error(`413 Payload Too Large Error: Request size was ${requestSize} MB`)

          // Try to extract more detailed error information
          let errorDetails = ''
          const payloadError = readRecord(errorData)
          const originalSize =
            typeof payloadError?.originalSize === 'number' ? payloadError.originalSize : null
          const limitSize =
            typeof payloadError?.limit === 'number' ? payloadError.limit : null

          if (originalSize !== null && limitSize !== null) {
            const originalSizeMb = Math.round((originalSize / 1024 / 1024) * 100) / 100
            const limitSizeMb = Math.round((limitSize / 1024 / 1024) * 100) / 100
            errorDetails = ` (${originalSizeMb}MB > ${limitSizeMb}MB limit)`
          }

          // Provide helpful suggestions to the user
          errorMessage = `Message too large${errorDetails}. Try: \n• Starting a new chat\n• Removing some images\n• Clearing chat history\n• Shortening your message`

          // Log message count and sizes to help diagnose
          try {
            const messages = (body as any).messages
            if (messages && Array.isArray(messages)) {
              safeConsole.error(`Message count: ${messages.length}`)

              let totalTextSize = 0
              let imageCount = 0

              // Log sizes of individual messages
              messages.forEach((msg, idx) => {
                let size = 0
                if (typeof msg.content === 'string') {
                  size = msg.content.length
                  totalTextSize += size
                } else if (Array.isArray(msg.content)) {
                  // Check for images in content
                  const textParts = msg.content.filter((part: any) => part.type === 'text')
                  const imageParts = msg.content.filter(
                    (part: any) => part.type === 'image' || part.type === 'image_url'
                  )

                  const textSize = textParts.reduce(
                    (acc: number, part: any) => acc + (part.text?.length || 0),
                    0
                  )
                  size = textSize
                  totalTextSize += textSize
                  imageCount += imageParts.length

                  safeConsole.error(
                    `Message #${idx} (${msg.role}): ${Math.round(size / 1024)}KB text, ${imageParts.length} images`
                  )
                  return
                }

                safeConsole.error(`Message #${idx} (${msg.role}): ${Math.round(size / 1024)}KB`)
                totalTextSize += size
              })

              safeConsole.error(
                `Total: ${Math.round(totalTextSize / 1024)}KB text, ${imageCount} images, ${requestSize}MB total`
              )

              // Suggest specific actions based on content analysis
              if (imageCount > 2) {
                errorMessage += ' (too many images)'
              } else if (messages.length > 20) {
                errorMessage += ' (too many messages)'
              } else if (totalTextSize > 500000) {
                errorMessage += ' (too much text)'
              }
            }
          } catch (err) {
            safeConsole.error('Error analyzing request content:', err)
          }
        }

        const statusToError = {
          404: { message: t('chat_helper.errors.model_missing'), logLevel: toast.error },
          429: {
            message: t('chat_helper.rate_limits.too_many_messages'),
            logLevel: toast.warning
          },
          402: {
            message: t('chat_helper.rate_limits.free_limit_reached'),
            logLevel: toast.warning
          },
          413: {
            message: t('chat_helper.rate_limits.message_too_long'),
            logLevel: toast.error
          },
          503: {
            message: t('chat_helper.rate_limits.provider_busy'),
            logLevel: toast.warning
          }
        }

        const errorInfo = statusToError[response.status as keyof typeof statusToError]

        if (errorInfo) {
          errorMessage = readChatApiErrorMessage(errorData, errorInfo.message || '')
          if (response.status !== 402) {
            errorLogLevel(errorMessage)
          }
        } else {
          errorMessage = readChatApiErrorMessage(
            errorData,
            t('chat_helper.errors.send_message')
          )
        }

        if (response.status === 402) {
          const paygOverageDetails = readPaygOverageDetails(errorData)
          if (paygOverageDetails) {
            showPaygOverageToast(paygOverageDetails, errorMessage)
            setIsGenerating(false)
            setChatMessages(removePendingAssistantTurn)
            throw new SubscriptionRequiredError(errorMessage)
          }

          // If Losi Max limit hit, fallback to Losi Mini automatically
          try {
            const payload = body as any
            const requestedModel = payload?.chatSettings?.model
            const limitDetails = readFreeLosiMaxLimitDetails(errorData)
            const canFallback = requestedModel === 'losi-omega' && Boolean(limitDetails)

            if (canFallback) {
              showFreeLosiMaxLimitToast({
                details: limitDetails,
                onSkipWait: () => setPaywallOpen?.(true)
              })
              const fallbackBody = {
                ...payload,
                chatSettings: {
                  ...payload.chatSettings,
                  model: 'losi-mini'
                }
              }
              try {
                onModelFallback?.('losi-mini')
              } catch {}
              const fallbackResponse = await fetchProtectedApi(url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...desktopAccessHeaders,
                  ...(desktopHost ? { 'expo-platform': 'electron' } : {}),
                  ...(desktopDeviceId ? { 'x-losi-desktop-device-id': desktopDeviceId } : {})
                },
                body: JSON.stringify(fallbackBody),
                signal: controller.signal
              })
              if (fallbackResponse.ok) {
                return fallbackResponse
              }
            }
          } catch (e) {
            // ignore and proceed to paywall
          }
          // No fallback possible or it failed – open paywall and stop
          setPaywallOpen?.(true)
          setIsGenerating(false)
          setChatMessages(removePendingAssistantTurn)
          throw new ChatResponseError(errorMessage)
        }
        if (response.status === 504 && retriesLeft > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
          return fetchWithRetry(retriesLeft - 1)
        }

        // Provider (Anthropic) capacity errors already survived retries with
        // backoff server-side; poll a few more times here, backing off
        // further each attempt, before surfacing the "high demand" toast.
        if (response.status === 503 && retriesLeft > 0) {
          const attempt = maxRetries - retriesLeft + 1
          await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt))
          return fetchWithRetry(retriesLeft - 1)
        }

        if (response.status === 409 && isGroupChatMentionOnlyErrorMessage(errorMessage)) {
          throw new ChatResponseError(errorMessage)
        }

        throw new ChatResponseError(errorMessage)
      }
      const sessionId = response.headers.get('X-s-ID')
      const modelId = response.headers.get('X-m-ID') || ''
      const providerId = response.headers.get('X-p-ID') || ''

      // TODO console.log("session ID is " + sessionId)

      if (sessionId) {
        storeSessionData({ sessionId, modelId, providerId })
      }
      return response
    } catch (error: any) {
      if (isAbortLikeError(error, controller.signal)) {
        throw error
      }

      if (
        error instanceof Error &&
        isGroupChatMentionOnlyErrorMessage(error.message)
      ) {
        throw error
      }

      const isHttpError =
        error instanceof ChatResponseError ||
        error instanceof SubscriptionRequiredError
      if (retriesLeft > 0 && !isHttpError) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
        return fetchWithRetry(retriesLeft - 1)
      }
      throw error
    }
  }

  try {
    return await fetchWithRetry(maxRetries)
  } catch (error: any) {
    if (isAbortLikeError(error, controller.signal)) {
      setIsGenerating(false)
      setChatMessages(removePendingAssistantTurn)
      throw error
    }

    setIsGenerating(false)
    if (
      error instanceof Error &&
      isGroupChatMentionOnlyErrorMessage(error.message)
    ) {
      setChatMessages(removePendingAssistantMessage)
      throw error
    }

    setChatMessages(removePendingAssistantTurn)
    // Transport failures (offline laptop, deploy-swap blip, circuit
    // breaker open) surface as raw TypeErrors like "Failed to fetch
    // (network circuit open)". After the retries above are exhausted,
    // translate them into a message a user can act on; the original
    // error name is preserved for downstream classification/logging.
    let surfacedError: unknown = error
    if (
      error instanceof Error &&
      (error.name === 'NetworkCircuitOpenError' ||
        isTransientNetworkError(error, controller.signal))
    ) {
      surfacedError = Object.assign(
        new Error(
          "Couldn't reach Losi — your connection dropped. Check your network and try again."
        ),
        { name: error.name }
      )
    }
    // Avoid noisy toast and error log when it's a subscription/paywall error
    if (!(error instanceof SubscriptionRequiredError)) {
      // Log name + message explicitly: Error objects serialize to "{}" in
      // session-replay console capture, which previously hid the actual
      // failure (e.g. Bedrock 503s) from every recording.
      safeConsole.error(
        t('chat_helper.errors.fetch_response_detail'),
        surfacedError instanceof Error
          ? `${surfacedError.name}: ${surfacedError.message}`
          : surfacedError
      )
      errorLogLevel(
        surfacedError instanceof Error ? surfacedError.message : errorMessage
      )
    }
    throw surfacedError
  }
}

export type SidekickToolActivePayload = {
  toolId: string
  entityType?: string
  entityId?: string
} | null

const MAX_SIDECAR_CONTINUATION_DEPTH = 480
const MAX_TOOL_RUN_CONTINUATION_DEPTH = 480
/** Auto-resume chat generation when the model hits the output token cap. */
const MAX_GENERATION_LENGTH_CONTINUATIONS = 6
const GENERATION_CONTINUE_USER_PROMPT =
  'Continue exactly where you left off. Do not repeat prior text. Output only the continuation.'
// A long streaming request held open across a WiFi/VPN handoff dies with a
// transient browser network error (ERR_NETWORK_CHANGED / "network error"),
// not an abort. When that happens mid-run the server-side work is already
// persisted as a chat_tool_run, so instead of hard-cancelling the turn we
// reconnect via the continuation endpoint. Bounded low so a genuinely
// offline user fails fast instead of retrying for minutes.
const MAX_TOOL_RUN_NETWORK_RESUME_ATTEMPTS = 6
const TOOL_RUN_NETWORK_RESUME_DELAY_MS = 1500

type PersistedToolRunForClient = {
  assistant_message_id?: string | null
  continuation_error?: string | null
  continuation_status?: string | null
  id?: string
  last_error?: string | null
  status?: string | null
}

type ToolRunClientTerminalStatus = 'completed' | 'failed' | 'running'

// Recovers the persisted chat_tool_run id from whatever annotations have
// streamed so far — emitted at run creation as `chat_tool_run.runId` and
// again on a deadline stop as `tool_run_continuation.runId`. Either lets us
// resume the exact same run.
function extractResumableToolRunId(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null

  const record = data as Record<string, unknown>
  const fromContinuation = record.tool_run_continuation
  if (
    fromContinuation &&
    typeof fromContinuation === 'object' &&
    typeof (fromContinuation as Record<string, unknown>).runId === 'string'
  ) {
    return (fromContinuation as Record<string, unknown>).runId as string
  }

  const fromRun = record.chat_tool_run
  if (
    fromRun &&
    typeof fromRun === 'object' &&
    typeof (fromRun as Record<string, unknown>).runId === 'string'
  ) {
    return (fromRun as Record<string, unknown>).runId as string
  }

  return null
}

function getChatIdFromMessage(message: ChatMessage): string | null {
  const chatId = (message.message as { chat_id?: unknown }).chat_id

  return typeof chatId === 'string' && chatId.trim() ? chatId : null
}

function getObjectRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function getToolRunClientStatus(
  run: PersistedToolRunForClient
): ToolRunClientTerminalStatus | null {
  if (run.status === 'completed' || run.continuation_status === 'completed') {
    return 'completed'
  }

  if (run.continuation_status === 'failed') {
    return 'failed'
  }

  if (run.continuation_status === 'running') {
    return 'running'
  }

  return null
}

function mergeToolRunContinuationClientData(args: {
  data: unknown
  error?: string | null
  runId: string
  status: ToolRunClientTerminalStatus
}) {
  const current = getObjectRecord(args.data)
  const existing = getObjectRecord(current.tool_run_continuation)

  return {
    ...current,
    tool_run_continuation: {
      ...existing,
      runId: args.runId,
      status: args.status,
      ...(args.error ? { error: args.error } : {})
    }
  }
}

function applyToolRunContinuationClientState(args: {
  data: unknown
  error?: string | null
  lastChatMessage: ChatMessage
  runId: string
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  status: ToolRunClientTerminalStatus
}) {
  const annotation = mergeToolRunContinuationClientData(args) as Json

  args.setChatMessages((prev) =>
    prev.map((chatMessage) => {
      if (chatMessage.message.id !== args.lastChatMessage.message.id) {
        return chatMessage
      }

      const currentMetadata = chatMessage.message.metadata as Json | null | undefined
      const metadata =
        args.status === 'completed'
          ? completePendingAssistantTurnMetadata(currentMetadata)
          : args.status === 'failed'
            ? failPendingAssistantTurnMetadata(currentMetadata)
            : currentMetadata ?? null

      return {
        ...chatMessage,
        message: {
          ...chatMessage.message,
          annotation: mergeAssistantMessageAnnotation(
            (chatMessage.message.annotation as Json | null | undefined) ?? null,
            annotation
          ),
          metadata
        }
      }
    })
  )
}

async function fetchPersistedToolRunForClient(args: {
  chatId: string
  runId: string
  signal: AbortSignal
}): Promise<PersistedToolRunForClient | null> {
  const response = await fetchProtectedApi(
    `/api/chat/tool-runs?chatId=${encodeURIComponent(args.chatId)}&limit=10`,
    { signal: args.signal }
  )

  if (!response.ok) {
    return null
  }

  const payload = (await response.json().catch(() => null)) as {
    data?: unknown
  } | null
  const runs = Array.isArray(payload?.data) ? payload.data : []

  return (
    runs.find(
      (run): run is PersistedToolRunForClient =>
        Boolean(run) &&
        typeof run === 'object' &&
        !Array.isArray(run) &&
        (run as PersistedToolRunForClient).id === args.runId
    ) ?? null
  )
}

async function refreshPersistedAssistantMessage(args: {
  chatId: string
  messageId: string
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  signal: AbortSignal
}) {
  const response = await fetchProtectedApi(
    `/api/chat/${encodeURIComponent(args.chatId)}/messages?limit=20`,
    { signal: args.signal }
  )

  if (!response.ok) {
    return false
  }

  const messages = (await response.json().catch(() => null)) as unknown
  const rows = Array.isArray(messages) ? messages : []
  const persisted = rows.find(
    (message): message is Tables<'messages'> =>
      Boolean(message) &&
      typeof message === 'object' &&
      !Array.isArray(message) &&
      (message as Tables<'messages'>).id === args.messageId
  )

  if (!persisted) {
    return false
  }

  args.setChatMessages((prev) =>
    prev.map((chatMessage) => {
      if (chatMessage.message.id !== args.messageId) {
        return chatMessage
      }

      return parseChatMessageCodeBlocksAndContent({
        ...chatMessage,
        codeBlocks: [],
        message: persisted
      })
    })
  )

  return true
}

async function recoverToolRunContinuationClientState(args: {
  data: unknown
  lastChatMessage: ChatMessage
  runId: string
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  signal: AbortSignal
}): Promise<{ data: unknown; handled: boolean }> {
  const chatId = getChatIdFromMessage(args.lastChatMessage)

  if (!chatId) {
    return { data: args.data, handled: false }
  }

  const run = await fetchPersistedToolRunForClient({
    chatId,
    runId: args.runId,
    signal: args.signal
  }).catch((error) => {
    safeConsole.warn('Failed to inspect persisted tool run', {
      error,
      runId: args.runId
    })
    return null
  })
  const status = run ? getToolRunClientStatus(run) : null

  if (!run || !status) {
    return { data: args.data, handled: false }
  }

  const error = run.continuation_error || run.last_error || null
  const nextData = mergeToolRunContinuationClientData({
    data: args.data,
    error,
    runId: args.runId,
    status
  })

  applyToolRunContinuationClientState({
    data: nextData,
    error,
    lastChatMessage: args.lastChatMessage,
    runId: args.runId,
    setChatMessages: args.setChatMessages,
    status
  })

  if (status === 'completed') {
    const messageId = run.assistant_message_id || args.lastChatMessage.message.id
    await refreshPersistedAssistantMessage({
      chatId,
      messageId,
      setChatMessages: args.setChatMessages,
      signal: args.signal
    }).catch((error) => {
      safeConsole.warn('Failed to refresh completed tool-run message', {
        error,
        messageId,
        runId: args.runId
      })
    })
  }

  return { data: nextData, handled: true }
}

// A dropped fetch surfaces as a TypeError (not an AbortError) — distinguish
// it from a real HTTP failure or a user abort so only genuinely transient
// connectivity blips trigger an auto-reconnect.
function isTransientNetworkError(error: unknown, signal?: AbortSignal): boolean {
  if (isAbortLikeError(error, signal)) return false
  if (!(error instanceof Error)) return false

  const name = error.name.toLowerCase()
  const message = error.message.toLowerCase()

  if (name === 'typeerror') {
    return (
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('load failed') ||
      message.includes('connection') ||
      message.includes('err_network')
    )
  }

  return message.includes('err_network_changed') || message.includes('network changed')
}

export function hasAssistantTurnOutput(message: ChatMessage | undefined): boolean {
  if (!message || message.message.role !== 'assistant') return false

  const content =
    typeof message.message.content === 'string'
      ? sanitizeAssistantVisibleContent(message.message.content).trim()
      : ''
  if (content.length > 0) return true

  if (Array.isArray(message.codeBlocks) && message.codeBlocks.length > 0) {
    return true
  }

  if (Array.isArray(message.fileItems) && message.fileItems.length > 0) {
    return true
  }

  if (
    Array.isArray(message.message.image_paths) &&
    message.message.image_paths.some(
      (imagePath) => typeof imagePath === 'string' && imagePath.trim().length > 0
    )
  ) {
    return true
  }

  const annotation =
    message.message.annotation && typeof message.message.annotation === 'object'
      ? (message.message.annotation as Record<string, unknown>)
      : null

  return Boolean(annotation && Object.keys(annotation).length > 0)
}

export function getPendingAssistantTurnCleanup(messages: ChatMessage[]): {
  nextMessages: ChatMessage[]
  restoredUserInput: string | null
  removedPendingAssistantTurn: boolean
} {
  const lastMessage = messages[messages.length - 1]

  if (!lastMessage || lastMessage.message.role !== 'assistant') {
    return {
      nextMessages: messages,
      restoredUserInput: null,
      removedPendingAssistantTurn: false
    }
  }

  if (hasAssistantTurnOutput(lastMessage)) {
    return {
      nextMessages: messages,
      restoredUserInput: null,
      removedPendingAssistantTurn: false
    }
  }

  const previousMessage = messages[messages.length - 2]
  if (previousMessage?.message.role === 'user') {
    return {
      nextMessages: messages.slice(0, -2),
      restoredUserInput:
        typeof previousMessage.message.content === 'string'
          ? previousMessage.message.content
          : null,
      removedPendingAssistantTurn: true
    }
  }

  return {
    nextMessages: messages.slice(0, -1),
    restoredUserInput: null,
    removedPendingAssistantTurn: true
  }
}

/**
 * Abort/stop cleanup scoped to THIS request's temp message ids.
 * Never touches a newer force-sent turn that already replaced the tail —
 * the unscoped helper would strip the wrong pair and ghost its draft.
 */
export function getRequestScopedPendingTurnCleanup(
  messages: ChatMessage[],
  request: {
    userMessageId?: string | null
    assistantMessageId?: string | null
  }
): {
  nextMessages: ChatMessage[]
  restoredUserInput: string | null
  removedPendingAssistantTurn: boolean
} {
  const assistantMessageId =
    typeof request.assistantMessageId === 'string' &&
    request.assistantMessageId.trim()
      ? request.assistantMessageId.trim()
      : null
  const userMessageId =
    typeof request.userMessageId === 'string' && request.userMessageId.trim()
      ? request.userMessageId.trim()
      : null

  if (!assistantMessageId && !userMessageId) {
    return getPendingAssistantTurnCleanup(messages)
  }

  const assistantIndex = assistantMessageId
    ? messages.findIndex(
        (chatMessage) => chatMessage.message.id === assistantMessageId
      )
    : -1

  if (assistantMessageId && assistantIndex < 0) {
    // This request's pending assistant is already gone (e.g. stop cleared it).
    // Do not mutate the current thread tail — it may belong to a newer send.
    return {
      nextMessages: messages,
      restoredUserInput: null,
      removedPendingAssistantTurn: false
    }
  }

  if (assistantIndex >= 0) {
    const assistantMessage = messages[assistantIndex]
    if (hasAssistantTurnOutput(assistantMessage)) {
      return {
        nextMessages: messages,
        restoredUserInput: null,
        removedPendingAssistantTurn: false
      }
    }
  }

  const idsToRemove = new Set<string>()
  if (assistantMessageId) idsToRemove.add(assistantMessageId)

  let restoredUserInput: string | null = null
  if (userMessageId) {
    const userMessage = messages.find(
      (chatMessage) => chatMessage.message.id === userMessageId
    )
    if (userMessage) {
      idsToRemove.add(userMessageId)
      restoredUserInput =
        typeof userMessage.message.content === 'string'
          ? userMessage.message.content
          : null
    }
  }

  if (idsToRemove.size === 0) {
    return {
      nextMessages: messages,
      restoredUserInput: null,
      removedPendingAssistantTurn: false
    }
  }

  return {
    nextMessages: messages.filter(
      (chatMessage) => !idsToRemove.has(chatMessage.message.id)
    ),
    restoredUserInput,
    removedPendingAssistantTurn: Boolean(assistantMessageId)
  }
}

function removePendingAssistantTurn(messages: ChatMessage[]): ChatMessage[] {
  return getPendingAssistantTurnCleanup(messages).nextMessages
}

function removePendingAssistantMessage(messages: ChatMessage[]): ChatMessage[] {
  const lastMessage = messages[messages.length - 1]

  if (!lastMessage || lastMessage.message.role !== 'assistant') {
    return messages
  }

  if (hasAssistantTurnOutput(lastMessage)) {
    return messages
  }

  return messages.slice(0, -1)
}

export const processResponse = async (
  response: Response,
  lastChatMessage: ChatMessage,
  isHosted: boolean,
  controller: AbortController,
  setFirstTokenReceived: React.Dispatch<React.SetStateAction<boolean>>,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setToolInUse: React.Dispatch<React.SetStateAction<string>>,
  selectedTools: Tables<'tools'>[] = [],
  setResponseTimeToFirstToken?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTimeTotal?: React.Dispatch<React.SetStateAction<number>>,
  setResponseTokensTotal?: React.Dispatch<React.SetStateAction<number>>,
  startTime = Date.now(),
  onSidekickToolActive?: (active: SidekickToolActivePayload) => void,
  continuationState?: {
    seedText?: string
    seedData?: unknown
    depth?: number
    networkResumeAttempts?: number
  }
): Promise<ProcessResponseResult> => {
  let fullText = continuationState?.seedText ?? ''
  let contentToAdd = ''
  let data: any = continuationState?.seedData ?? null
  let generationFinishReason: string | null = null

  // Set when an inline tool marker fires; consumed by the next text append so
  // that post-tool text starts on its own line in the PERSISTED content. This
  // is a belt-and-suspenders boundary in the content itself so that even if
  // the positional render_segments are unavailable on a given render path,
  // "...right now." and "Good first pass." do not glue together on reload.
  let pendingToolTextBreak = false

  // When a tool marker arrives before any assistant text has streamed, we still
  // need a stable "text-0" segment that can be cleanly closed at offset 0.
  // This prevents tool blocks from falling back to the "stacked at top" path.
  let hasInitializedInlineSegments = false

  // Track distinct reasoning runs so each one persists as its OWN inline
  // segment at the point in the text where it occurred. Without this, all
  // reasoning collapses into a single "Thought for Ns" block at the top on
  // reload. We record a reasoning segment when a finalized reasoning_summary
  // changes to a new value, keyed at the current end of the streamed text.
  let lastRecordedReasoningText: string | null = null
  let reasoningRunSequenceBase = 100000
  const reasoningRunStartedAt = new Map<string, number>()
  // One-shot so stream reasoning annotations do not re-emit thinking.start.
  let inlineReasoningStarted = false

  const sessionId = response.headers.get('X-s-ID')
  const modelId = response.headers.get('X-m-ID') || ''
  const providerId = response.headers.get('X-p-ID') || ''
  const continuationDepth = continuationState?.depth ?? 0
  const networkResumeAttempts = continuationState?.networkResumeAttempts ?? 0

  // TODO console.log("session ID is " + sessionId)

  if (sessionId) {
    storeSessionData({ sessionId, modelId, providerId })
  }
  if (response.body) {
    try {
      const looksLikeDataStreamFragment = (value: string) =>
        /^[0-9]: ?(?:"|\{|\[)/.test(value)

      const applyParsedLine = (line: string, fallbackSuffix = '\n') => {
        try {
          const { text, data: newData } = parseDataStream(line)
          if (newData) {
            // Inline tool markers are a project-specific stream extension.
            // Only treat these markers as special; pass through all other ai stream
            // parts (including tool_calls/message_annotations) to the normal merge path.
            const maybeMarker =
              typeof newData === 'object' && newData != null
                ? (newData as Record<string, unknown>).__inline_tool_marker
                : undefined

            const isInlineToolMarker =
              maybeMarker != null &&
              typeof maybeMarker === 'object' &&
              'kind' in (maybeMarker as Record<string, unknown>) &&
              (maybeMarker as Record<string, unknown>).kind === 'tool_marker' &&
              'event' in (maybeMarker as Record<string, unknown>) &&
              (maybeMarker as Record<string, unknown>).event != null &&
              typeof (maybeMarker as Record<string, unknown>).event === 'object'

            if (isInlineToolMarker) {
              const marker = maybeMarker as {
                kind: 'tool_marker'
                event: {
                  type: 'tool.start' | 'tool.progress' | 'tool.complete' | 'tool.error'
                  toolId?: string
                  toolName?: string
                  sequence?: number
                  request?: unknown
                  result?: unknown
                  error?: string
                  progress?: { message?: string; percent?: number; step?: string }
                }
              }

              const event = marker.event
              const toolId = typeof event.toolId === 'string' ? event.toolId : null
              const toolName = typeof event.toolName === 'string' ? event.toolName : null

              if (toolId && toolName) {
                const now = Date.now()

                // Dispatch inline tool event directly to server events listeners
                // so LoadingMessage picks it up instantly (no Supabase Realtime delay)
                try {
                  dispatchInlineToolEvent(event.type as any, {
                    ...event,
                    chatId: chatId || undefined,
                    timestamp: now
                  })
                } catch {
                  // Non-critical: LoadingMessage will still get it via Realtime
                }

                // Ensure we have a stable baseline text segment even if the tool
                // starts before any streamed text arrives (offset 0).
                if (!hasInitializedInlineSegments) {
                  data = (() => {
                    const current = data && typeof data === 'object' ? data : {}
                    const existingSegmentsRaw = (current as any).render_segments
                    const existingSegments = Array.isArray(existingSegmentsRaw)
                      ? existingSegmentsRaw
                      : []
                    if (existingSegments.length === 0) {
                      ;(current as any).render_segments = [
                        {
                          id: 'text-0',
                          type: 'text',
                          sequence: 0,
                          startOffset: 0
                        }
                      ]
                    }
                    return current
                  })()
                  hasInitializedInlineSegments = true
                }

                const toolTextOffset = fullText.length

                // Next text run should start on its own line in persisted content.
                pendingToolTextBreak = true

                const isAutoExpandedTool =
                  toolName.includes('googleSearch') ||
                  toolName.includes('webScraper__googleSearch') ||
                  toolName.includes('unifiedImage') ||
                  toolName.includes('imageGeneration') ||
                  toolName.includes('imageEdit') ||
                  toolName.includes('flux1Pro') ||
                  toolName.includes('videoGeneration') ||
                  toolName.includes('videoGenerator') ||
                  toolName.includes('getWeather') ||
                  toolName.includes('webScraper__getWeather')

                const stableToolId = toolId
                const existingOrderRaw = (data as any)?.tool_first_seen_order
                const existingOrder = Array.isArray(existingOrderRaw)
                  ? (existingOrderRaw as any[]).filter((x) => typeof x === 'string')
                  : []

                const orderIndex = existingOrder.indexOf(stableToolId)
                const isNewToolRun = orderIndex === -1
                const nextOrder = isNewToolRun
                  ? [...existingOrder, stableToolId]
                  : existingOrder
                const stableIndex = isNewToolRun ? existingOrder.length : orderIndex

                const toolSegmentSequence = stableIndex * 2 + 1
                const afterTextSequence = toolSegmentSequence + 1

                const ensureSegments = (prev: any) => {
                  const current = prev && typeof prev === 'object' ? prev : {}
                  ;(current as any).tool_first_seen_order = nextOrder

                  const existingSegmentsRaw = (current as any).render_segments
                  const existingSegments = Array.isArray(existingSegmentsRaw)
                    ? existingSegmentsRaw
                    : []
                  const base =
                    existingSegments.length > 0
                      ? existingSegments
                      : [
                          {
                            id: 'text-0',
                            type: 'text',
                            sequence: 0,
                            startOffset: 0
                          }
                        ]

                  const hasTool = base.some(
                    (s: any) => s && s.type === 'tool' && s.toolId === stableToolId
                  )
                  if (!hasTool) {
                    // Close EVERY open text segment at/before this offset —
                    // not just the last by sequence. Reasoning text-after
                    // segments use high sequence numbers and used to steal the
                    // single close, leaving tool text-after segments open-ended
                    // (which re-painted the whole answer after every tool card).
                    closeOpenTextSegmentsAt(base as Array<Record<string, unknown>>, toolTextOffset)

                    base.push({
                      id: `tool-${stableToolId}`,
                      type: 'tool',
                      sequence: toolSegmentSequence,
                      toolId: stableToolId,
                      textOffset: toolTextOffset
                    })

                    // Reuse an existing open/empty text-after at this offset
                    // instead of stacking duplicates that all run to EOF.
                    const existingAfter = base.find(
                      (s: any) =>
                        s &&
                        s.type === 'text' &&
                        typeof s.startOffset === 'number' &&
                        s.startOffset === toolTextOffset &&
                        (s.endOffset == null || s.endOffset === toolTextOffset)
                    )
                    if (existingAfter) {
                      delete (existingAfter as any).endOffset
                    } else {
                      base.push({
                        id: `text-after-${stableToolId}`,
                        type: 'text',
                        sequence: afterTextSequence,
                        startOffset: toolTextOffset
                      })
                    }
                  }

                  base.sort((a: any, b: any) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
                  ;(current as any).render_segments = base
                  return current
                }

                const ensureTimeline = (prev: any) => {
                  const current = prev && typeof prev === 'object' ? prev : {}
                  const existingTimelineRaw = (current as any).tool_timeline
                  const existingTimeline = Array.isArray(existingTimelineRaw)
                    ? existingTimelineRaw
                    : []
                  const index = existingTimeline.findIndex((e: any) => e?.id === stableToolId)

                  const baseEntry = index >= 0 ? existingTimeline[index] : null
                  const startedAt =
                    baseEntry && typeof baseEntry.startedAt === 'number'
                      ? baseEntry.startedAt
                      : now
                  const seq =
                    typeof baseEntry?.sequence === 'number'
                      ? baseEntry.sequence
                      : typeof event.sequence === 'number'
                        ? event.sequence
                        : 0

                  const nextEntry: any = {
                    id: stableToolId,
                    name: toolName,
                    sequence: seq,
                    startedAt,
                    isExpanded: Boolean(baseEntry?.isExpanded ?? isAutoExpandedTool)
                  }

                  if (event.type === 'tool.start') {
                    nextEntry.status = 'running'
                    nextEntry.request = event.request
                  } else if (event.type === 'tool.progress') {
                    nextEntry.status =
                      baseEntry?.status === 'complete' || baseEntry?.status === 'error'
                        ? baseEntry.status
                        : 'progress'
                    nextEntry.progress = {
                      ...(baseEntry?.progress ?? {}),
                      ...(event.progress ?? {})
                    }
                  } else if (event.type === 'tool.complete') {
                    nextEntry.status = 'complete'
                    nextEntry.endedAt = now
                    nextEntry.result = event.result
                    // Memory writes must bust the prompt warm cache or the next
                    // send can omit a fact just stored this turn.
                    const toolName = String(event.toolName || '')
                    if (
                      /memory|remember|storeMemory/i.test(toolName) ||
                      toolName.includes('storeMemoryToRemember')
                    ) {
                      invalidatePromptMemoriesCache({
                        chatId: lastChatMessage.message.chat_id || null,
                        userId: lastChatMessage.message.user_id || null
                      })
                    }
                  } else if (event.type === 'tool.error') {
                    const errorMessage =
                      typeof event.error === 'string' && event.error.trim()
                        ? event.error.trim()
                        : 'Tool execution failed'
                    nextEntry.status = 'error'
                    nextEntry.endedAt = now
                    nextEntry.error = errorMessage
                    nextEntry.result = {
                      success: false,
                      error: errorMessage,
                      code: 'TOOL_EXECUTION_ERROR',
                      message: errorMessage
                    }
                  }

                  if (index >= 0) {
                    existingTimeline[index] = { ...(baseEntry ?? {}), ...nextEntry }
                  } else {
                    existingTimeline.push(nextEntry)
                  }

                  existingTimeline.sort((a: any, b: any) => {
                    const aSeq = typeof a?.sequence === 'number' ? a.sequence : 0
                    const bSeq = typeof b?.sequence === 'number' ? b.sequence : 0
                    if (aSeq !== bSeq) return aSeq - bSeq
                    const aStart = typeof a?.startedAt === 'number' ? a.startedAt : 0
                    const bStart = typeof b?.startedAt === 'number' ? b.startedAt : 0
                    return aStart - bStart
                  })
                  ;(current as any).tool_timeline = existingTimeline
                  return current
                }

                data = ensureTimeline(ensureSegments(data))

                // Tool markers do not contribute text output
                return
              }
            }

            const toMerge = Array.isArray(newData)
              ? (newData as unknown[]).reduce<Record<string, unknown>>(
                  (acc, item) => mergeStreamAnnotationData(acc, item),
                  {}
                )
              : newData
            const toMergeRecord = mergeStreamAnnotationData({}, toMerge)
            data = mergeStreamAnnotationData(data, toMergeRecord)

            const finishFromAnnotation = toMergeRecord.generation_finish_reason
            if (typeof finishFromAnnotation === 'string' && finishFromAnnotation.trim()) {
              generationFinishReason = finishFromAnnotation.trim()
            }

            // Mirror Anthropic/OpenAI reasoning stream annotations into the
            // pending LoadingMessage via inline server-events (same pattern as
            // tool markers). Realtime is still authoritative for start/end, but
            // stream annotations update Thought text with zero Realtime lag.
            try {
              const streamReasoning = toMergeRecord.reasoning_summary_stream
              const finalReasoning = toMergeRecord.reasoning_summary
              const streamText =
                streamReasoning &&
                typeof streamReasoning === 'object' &&
                typeof (streamReasoning as Record<string, unknown>).text === 'string'
                  ? ((streamReasoning as Record<string, unknown>).text as string)
                  : null
              const finalText =
                finalReasoning &&
                typeof finalReasoning === 'object' &&
                typeof (finalReasoning as Record<string, unknown>).text === 'string'
                  ? ((finalReasoning as Record<string, unknown>).text as string)
                  : null

              if (streamText) {
                const now = Date.now()
                if (!inlineReasoningStarted) {
                  inlineReasoningStarted = true
                  dispatchInlineToolEvent('message.thinking.start', {
                    chatId: chatId || undefined,
                    message: 'Thinking…',
                    timestamp: now
                  })
                }
                dispatchInlineToolEvent('message.thinking.update', {
                  chatId: chatId || undefined,
                  message: streamText.slice(-4000),
                  timestamp: now
                })
              }

              if (finalText) {
                inlineReasoningStarted = false
                dispatchInlineToolEvent('message.thinking.end', {
                  chatId: chatId || undefined,
                  summary: finalText,
                  timestamp: Date.now()
                })
              }
            } catch {
              // Non-critical: LoadingMessage still gets Realtime thinking events.
            }

            // Inline tool markers can also arrive merged as a normal annotation
            // key (not only via the dedicated early-return branch above). When
            // that happens the tool result lands as a flat annotation key with
            // NO render_segments/tool_timeline, so on reload the message falls
            // back to the "everything stacked at the top" ToolActivityLog path.
            // Build the positional tool segment + timeline entry here too, keyed
            // at the current end of the streamed text, so tools render INLINE at
            // their execution point and persist that way.
            try {
              const mergedMarker = (toMergeRecord as Record<string, unknown>)
                .__inline_tool_marker
              const markerEvent =
                mergedMarker &&
                typeof mergedMarker === 'object' &&
                (mergedMarker as Record<string, unknown>).kind === 'tool_marker'
                  ? ((mergedMarker as Record<string, unknown>).event as
                      | {
                          type?: string
                          toolId?: string
                          toolName?: string
                          sequence?: number
                          request?: unknown
                          result?: unknown
                          error?: string
                          progress?: {
                            message?: string
                            percent?: number
                            step?: string
                          }
                        }
                      | undefined)
                  : undefined

              if (markerEvent && typeof markerEvent === 'object') {
                const mToolId =
                  typeof markerEvent.toolId === 'string' && markerEvent.toolId
                    ? markerEvent.toolId
                    : typeof markerEvent.toolName === 'string' && markerEvent.toolName
                      ? markerEvent.toolName
                      : 'tool'
                const mToolName =
                  typeof markerEvent.toolName === 'string' && markerEvent.toolName
                    ? markerEvent.toolName
                    : mToolId
                const mType = markerEvent.type ?? 'tool.complete'
                const now = Date.now()
                const mTextOffset = fullText.length

                const current = data && typeof data === 'object' ? data : {}

                // --- render_segments (position) ---
                const existingOrderRaw = (current as any).tool_first_seen_order
                const existingOrder = Array.isArray(existingOrderRaw)
                  ? (existingOrderRaw as any[]).filter((x) => typeof x === 'string')
                  : []
                const orderIndex = existingOrder.indexOf(mToolId)
                const isNewToolRun = orderIndex === -1
                const nextOrder = isNewToolRun
                  ? [...existingOrder, mToolId]
                  : existingOrder
                const stableIndex = isNewToolRun ? existingOrder.length : orderIndex
                const toolSeq = stableIndex * 2 + 1
                ;(current as any).tool_first_seen_order = nextOrder

                const segsRaw = (current as any).render_segments
                const segs = Array.isArray(segsRaw)
                  ? segsRaw
                  : [{ id: 'text-0', type: 'text', sequence: 0, startOffset: 0 }]
                const hasToolSeg = segs.some(
                  (s: any) => s && s.type === 'tool' && s.toolId === mToolId
                )
                if (!hasToolSeg) {
                  closeOpenTextSegmentsAt(segs as Array<Record<string, unknown>>, mTextOffset)
                  segs.push({
                    id: `tool-${mToolId}`,
                    type: 'tool',
                    sequence: toolSeq,
                    toolId: mToolId,
                    textOffset: mTextOffset
                  })
                  const existingAfter = segs.find(
                    (s: any) =>
                      s &&
                      s.type === 'text' &&
                      typeof s.startOffset === 'number' &&
                      s.startOffset === mTextOffset &&
                      (s.endOffset == null || s.endOffset === mTextOffset)
                  )
                  if (existingAfter) {
                    delete (existingAfter as any).endOffset
                  } else {
                    segs.push({
                      id: `text-after-${mToolId}`,
                      type: 'text',
                      sequence: toolSeq + 1,
                      startOffset: mTextOffset
                    })
                  }
                  segs.sort((a: any, b: any) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
                }
                ;(current as any).render_segments = segs

                // --- tool_timeline (state/result) ---
                const isAutoExpanded =
                  mToolName.includes('googleSearch') ||
                  mToolName.includes('webScraper__googleSearch') ||
                  mToolName.includes('getWeather') ||
                  mToolName.includes('webScraper__getWeather') ||
                  mToolName.includes('unifiedImage') ||
                  mToolName.includes('imageGeneration') ||
                  mToolName.includes('imageEdit') ||
                  mToolName.includes('flux1Pro') ||
                  mToolName.includes('videoGeneration') ||
                  mToolName.includes('videoGenerator')
                const tlRaw = (current as any).tool_timeline
                const tl = Array.isArray(tlRaw) ? tlRaw : []
                const tlIndex = tl.findIndex((e: any) => e?.id === mToolId)
                const baseEntry = tlIndex >= 0 ? tl[tlIndex] : null
                const startedAt =
                  baseEntry && typeof baseEntry.startedAt === 'number'
                    ? baseEntry.startedAt
                    : now
                const nextEntry: any = {
                  id: mToolId,
                  name: mToolName,
                  sequence:
                    typeof baseEntry?.sequence === 'number'
                      ? baseEntry.sequence
                      : typeof markerEvent.sequence === 'number'
                        ? markerEvent.sequence
                        : stableIndex,
                  startedAt,
                  isExpanded: Boolean(baseEntry?.isExpanded ?? isAutoExpanded)
                }
                if (mType === 'tool.start') {
                  nextEntry.status = 'running'
                  nextEntry.request = markerEvent.request
                } else if (mType === 'tool.progress') {
                  nextEntry.status =
                    baseEntry?.status === 'complete' || baseEntry?.status === 'error'
                      ? baseEntry.status
                      : 'progress'
                  nextEntry.progress = {
                    ...(baseEntry?.progress ?? {}),
                    ...(markerEvent.progress ?? {})
                  }
                } else if (mType === 'tool.complete') {
                  nextEntry.status = 'complete'
                  nextEntry.endedAt = now
                  nextEntry.result = markerEvent.result
                } else if (mType === 'tool.error') {
                  const errorMessage =
                    typeof markerEvent.error === 'string' && markerEvent.error.trim()
                      ? markerEvent.error.trim()
                      : 'Tool execution failed'
                  nextEntry.status = 'error'
                  nextEntry.endedAt = now
                  nextEntry.error = errorMessage
                  nextEntry.result = {
                    success: false,
                    error: errorMessage,
                    code: 'TOOL_EXECUTION_ERROR',
                    message: errorMessage
                  }
                }
                if (tlIndex >= 0) {
                  tl[tlIndex] = { ...(baseEntry ?? {}), ...nextEntry }
                } else {
                  tl.push(nextEntry)
                }
                tl.sort((a: any, b: any) => {
                  const aSeq = typeof a?.sequence === 'number' ? a.sequence : 0
                  const bSeq = typeof b?.sequence === 'number' ? b.sequence : 0
                  if (aSeq !== bSeq) return aSeq - bSeq
                  const aStart = typeof a?.startedAt === 'number' ? a.startedAt : 0
                  const bStart = typeof b?.startedAt === 'number' ? b.startedAt : 0
                  return aStart - bStart
                })
                ;(current as any).tool_timeline = tl

                pendingToolTextBreak = true
                data = current
              }
            } catch {
              // Non-critical: falls back to the flat ToolActivityLog render.
            }

            // Record each distinct finalized reasoning run as its own inline
            // segment so it renders in place on reload instead of collapsing
            // into one top "Thought for Ns" block. The reasoning for a run is
            // emitted before the text it produces, so we key it at the current
            // end of the streamed text (fullText.length).
            try {
              const reasoningSummary =
                data && typeof data === 'object'
                  ? (data as Record<string, unknown>).reasoning_summary
                  : undefined
              const reasoningText =
                reasoningSummary &&
                typeof reasoningSummary === 'object' &&
                typeof (reasoningSummary as Record<string, unknown>).text === 'string'
                  ? ((reasoningSummary as Record<string, unknown>).text as string).trim()
                  : ''

              if (reasoningText && reasoningText !== lastRecordedReasoningText) {
                lastRecordedReasoningText = reasoningText
                const reasoningTextOffset = fullText.length
                const current = data && typeof data === 'object' ? data : {}
                const existingSegmentsRaw = (current as any).render_segments
                const existingSegments = Array.isArray(existingSegmentsRaw)
                  ? existingSegmentsRaw
                  : []
                const base =
                  existingSegments.length > 0
                    ? existingSegments
                    : [{ id: 'text-0', type: 'text', sequence: 0, startOffset: 0 }]

                // Prefer updating the open reasoning run at this text offset so
                // short early drafts are not left as extra collapsed stubs.
                const liveReasoningIndex = [...base]
                  .map((segment, index) => ({ segment, index }))
                  .reverse()
                  .find(
                    ({ segment }) =>
                      segment &&
                      segment.type === 'reasoning' &&
                      segment.textOffset === reasoningTextOffset
                  )?.index

                if (typeof liveReasoningIndex === 'number') {
                  const liveReasoning = base[liveReasoningIndex] as Record<string, unknown>
                  const runId =
                    typeof liveReasoning.id === 'string'
                      ? liveReasoning.id
                      : `reasoning-${liveReasoningIndex}`
                  if (!reasoningRunStartedAt.has(runId)) {
                    reasoningRunStartedAt.set(runId, Date.now())
                  }
                  base[liveReasoningIndex] = {
                    ...liveReasoning,
                    id: runId,
                    text: reasoningText,
                    durationMs: Math.max(
                      0,
                      Date.now() - (reasoningRunStartedAt.get(runId) ?? Date.now())
                    )
                  }
                  ;(current as any).render_segments = base
                  data = current
                } else {
                  const runId = `reasoning-${base.filter((s: any) => s?.type === 'reasoning').length}`
                  if (!reasoningRunStartedAt.has(runId)) {
                    reasoningRunStartedAt.set(runId, Date.now())
                  }

                  // Close every open text run at this offset so the reasoning
                  // block slots between the preceding and following text.
                  closeOpenTextSegmentsAt(
                    base as Array<Record<string, unknown>>,
                    reasoningTextOffset
                  )

                  const reasoningSequence = reasoningRunSequenceBase++
                  base.push({
                    id: runId,
                    type: 'reasoning',
                    sequence: reasoningSequence,
                    textOffset: reasoningTextOffset,
                    text: reasoningText,
                    durationMs: Math.max(
                      0,
                      Date.now() - (reasoningRunStartedAt.get(runId) ?? Date.now())
                    )
                  })
                  const existingAfterReasoning = base.find(
                    (s: any) =>
                      s &&
                      s.type === 'text' &&
                      typeof s.startOffset === 'number' &&
                      s.startOffset === reasoningTextOffset &&
                      (s.endOffset == null || s.endOffset === reasoningTextOffset)
                  )
                  if (existingAfterReasoning) {
                    delete (existingAfterReasoning as any).endOffset
                  } else {
                    base.push({
                      id: `text-after-${runId}`,
                      type: 'text',
                      sequence: reasoningSequence + 1,
                      startOffset: reasoningTextOffset
                    })
                  }
                  base.sort((a: any, b: any) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
                  ;(current as any).render_segments = base
                  data = current
                }
              }
            } catch {
              // Non-critical: reasoning still renders via reasoning_summary
              // fallback if segment building fails.
            }

            const sidekickToolActive = toMergeRecord.sidekick_tool_active
            if (
              sidekickToolActive &&
              typeof sidekickToolActive === 'object' &&
              !Array.isArray(sidekickToolActive)
            ) {
              const activeRecord = sidekickToolActive as Record<string, unknown>
              if (typeof activeRecord.toolId === 'string') {
                onSidekickToolActive?.({
                  toolId: activeRecord.toolId,
                  entityType:
                    typeof activeRecord.entityType === 'string'
                      ? activeRecord.entityType
                      : undefined,
                  entityId:
                    typeof activeRecord.entityId === 'string'
                      ? activeRecord.entityId
                      : undefined
                })
              }
            }
            if (toMergeRecord.sidekick_tool_done) onSidekickToolActive?.(null)
          }
          if (pendingToolTextBreak && text && text.length > 0) {
            // A tool ran; this is the first text after it. Ensure it begins on
            // its own line in the persisted content (unless a break is already
            // present on either side), so sentences don't glue across the tool.
            const needsBreak =
              !fullText.endsWith('\n') && !text.startsWith('\n')
            if (needsBreak && fullText.length > 0) {
              fullText += '\n\n'
            }
            pendingToolTextBreak = false
          }
          // Dedupe snapshot replays (same bug as server output_text.done dumps
          // after tool rounds) so repetition never lands in the DB row.
          fullText = appendStreamTextDelta(fullText, text)
        } catch {
          fullText = appendStreamTextDelta(fullText, line + fallbackSuffix)
        }
      }

      let bufferedStreamFragment = ''

      const flushBufferedLines = () => {
        if (bufferedStreamFragment.length === 0) return

        const hasTrailingNewline = bufferedStreamFragment.endsWith('\n')
        const pendingLines = bufferedStreamFragment.split('\n')
        const trailingFragment = hasTrailingNewline ? '' : pendingLines.pop() ?? ''

        bufferedStreamFragment = ''

        for (const rawLine of pendingLines) {
          const line = rawLine.trimEnd()
          if (line === '') continue
          applyParsedLine(line)
        }

        if (trailingFragment.length === 0) {
          return
        }

        if (!looksLikeDataStreamFragment(trailingFragment)) {
          fullText = appendStreamTextDelta(fullText, trailingFragment)
          return
        }

        bufferedStreamFragment = trailingFragment
      }

      const tryFlushCompleteBufferedFrame = () => {
        if (!looksLikeDataStreamFragment(bufferedStreamFragment)) {
          return false
        }

        try {
          parseDataStream(bufferedStreamFragment)
        } catch {
          return false
        }

        applyParsedLine(bufferedStreamFragment, '')
        bufferedStreamFragment = ''
        return true
      }

      /**
       * Flush every complete data-stream frame immediately. Prefer newline
       * splits (canonical AI SDK framing), then also commit a trailing
       * complete frame that arrived without its terminating newline so text
       * deltas paint as soon as they are receivable — not after buffering.
       */
      const flushAvailableStreamFrames = () => {
        if (bufferedStreamFragment.includes('\n')) {
          flushBufferedLines()
        }
        if (bufferedStreamFragment.length > 0) {
          tryFlushCompleteBufferedFrame()
        }
        if (
          bufferedStreamFragment.length > 0 &&
          !looksLikeDataStreamFragment(bufferedStreamFragment)
        ) {
          fullText = appendStreamTextDelta(fullText, bufferedStreamFragment)
          bufferedStreamFragment = ''
        }
      }

      await consumeReadableStream(
        response.body,
        (chunk) => {
          const sessionId = getAndClearSessionId()
          const modelId = getAndClearModelId()
          const providerId = getAndClearProviderId()
          const previousFullText = fullText
          let hasChunkContent = false
          try {
            contentToAdd = isHosted
              ? chunk
              : // Ollama's streaming endpoint returns new-line separated JSON
                // objects. A chunk may have more than one of these objects, so we
                // need to split the chunk by new-lines and handle each one
                // separately.
                chunk
                  .trimEnd()
                  .split('\n')
                  .reduce((acc, line) => acc + JSON.parse(line).message.content, '')

            if (contentToAdd === '') {
              return
            }

            hasChunkContent = true

            // Always parse stream when we have newlines so annotations (losi_steps, show_plan, etc.) are merged even with no tools selected
            bufferedStreamFragment += contentToAdd
            flushAvailableStreamFrames()
          } catch (error) {
            safeConsole.error('Error parsing JSON:', error)
          }

          if (!hasChunkContent) {
            return
          }

          const receivedAssistantOutput = fullText !== previousFullText

          if (receivedAssistantOutput) {
            setResponseTimeToFirstToken?.((prev) => {
              if (prev === 0) {
                return (Date.now() - startTime) / 1000
              }
              return prev
            })
            setFirstTokenReceived(true)
            setToolInUse('none')
          }

          setResponseTimeTotal?.((prev) => (Date.now() - startTime) / 1000)

          setChatMessages((prev) =>
            prev.map((chatMessage) => {
              if (chatMessage.message.id !== lastChatMessage.message.id) {
                return chatMessage
              }

              const nextAnnotation =
                Array.isArray(data)
                  ? ([...data] as Json)
                  : data && typeof data === 'object'
                    ? ({ ...(data as Record<string, Json>) } as Json)
                    : ((data ?? null) as Json)
              // Approvals must update live — don't wait for a full reload.
              if (isAwaitingShowPlanPayload(nextAnnotation)) {
                notifyCrewApprovalsRefresh()
              }
              const hasResponseControlMarker =
                hasAssistantResponseControlMarker(fullText)
              const parsedResponseControls = hasResponseControlMarker
                ? parseSuggestions(fullText)
                : null
              const streamedContent = sanitizeAssistantVisibleContent(
                parsedResponseControls ? parsedResponseControls.content : fullText
              )
              const currentMetadata = chatMessage.message.metadata as Record<
                string,
                unknown
              > | null
              const nextMetadata = parsedResponseControls
                ? mergeAssistantResponseControlMetadata(currentMetadata, parsedResponseControls)
                : currentMetadata
              const hasCodeFence = streamedContent.includes('```')
              const parsedCode = hasCodeFence
                ? parseCodeBlocksAndContent(
                    streamedContent,
                    chatMessage.message.id
                  )
                : null

              return {
                message: {
                  ...chatMessage.message,
                  content: parsedCode ? parsedCode.parsedContent : streamedContent,
                  annotation: mergeAssistantMessageAnnotation(
                    (chatMessage.message.annotation as Json | null | undefined) ?? null,
                    nextAnnotation
                  ),
                  session_id: sessionId,
                  model_id: modelId,
                  provider_id: providerId,
                  metadata: nextMetadata as Json | null
                },
                fileItems: chatMessage.fileItems,
                codeBlocks: parsedCode ? parsedCode.codeBlocks : chatMessage.codeBlocks
              }
            })
          )
        },
        controller.signal
      )

      if (bufferedStreamFragment.length > 0) {
        if (looksLikeDataStreamFragment(bufferedStreamFragment)) {
          applyParsedLine(bufferedStreamFragment, '')
        } else {
          fullText = appendStreamTextDelta(fullText, bufferedStreamFragment)
        }
      }

      function findSkipTokenCount(data: any): boolean {
        if (!data) return false

        // Handle array of items (or nested arrays)
        if (Array.isArray(data)) {
          return data.some((item) => findSkipTokenCount(item))
        }

        // Handle plain objects – walk values recursively and look for skipTokenCount
        if (typeof data === 'object') {
          for (const key of Object.keys(data)) {
            const value = data[key]
            if (value && typeof value === 'object') {
              if ((value as any).skipTokenCount) {
                return true
              }
              if (findSkipTokenCount(value)) {
                return true
              }
            }
          }
        }

        return false
      }

      if (setResponseTokensTotal) {
        setResponseTokensTotal((prev) => {
          if (!findSkipTokenCount(data)) {
            const previousLength = continuationState?.seedText?.length ?? 0
            return prev + countTokens(fullText.slice(previousLength))
          }
          return prev
        })
      }

      const rawContinuation =
        data && typeof data === 'object' ? (data as any).sidecar_continuation : null
      const sidecarContinuation =
        rawContinuation &&
        typeof rawContinuation === 'object' &&
        typeof rawContinuation.runId === 'string' &&
        rawContinuation.status === 'pending'
          ? { runId: rawContinuation.runId as string }
          : null

      if (
        sidecarContinuation &&
        continuationDepth < MAX_SIDECAR_CONTINUATION_DEPTH
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 750))

        const continuationResponse = await fetchProtectedApi('/api/chat/sidecar/continue', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            runId: sidecarContinuation.runId
          }),
          signal: controller.signal
        })

        if (!continuationResponse.ok) {
          throw new Error('Failed to continue Sidecar run')
        }

        return processResponse(
          continuationResponse,
          lastChatMessage,
          true,
          controller,
          setFirstTokenReceived,
          setChatMessages,
          setToolInUse,
          selectedTools,
          setResponseTimeToFirstToken,
          setResponseTimeTotal,
          setResponseTokensTotal,
          startTime,
          onSidekickToolActive,
          {
            seedText: fullText,
            seedData: data,
            depth: continuationDepth + 1
          }
        )
      }

      if (sidecarContinuation) {
        throw new Error('Sidecar continuation exceeded the safe retry limit')
      }

      const rawToolRunContinuation =
        data && typeof data === 'object' ? (data as any).tool_run_continuation : null
      const toolRunContinuation =
        rawToolRunContinuation &&
        typeof rawToolRunContinuation === 'object' &&
        typeof rawToolRunContinuation.runId === 'string' &&
        rawToolRunContinuation.status === 'pending'
          ? { runId: rawToolRunContinuation.runId as string }
          : null

      if (
        toolRunContinuation &&
        continuationDepth < MAX_TOOL_RUN_CONTINUATION_DEPTH
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 750))

        const recovered = await recoverToolRunContinuationClientState({
          data,
          lastChatMessage,
          runId: toolRunContinuation.runId,
          setChatMessages,
          signal: controller.signal
        })

        if (recovered.handled) {
          data = recovered.data
          return {
            generatedText: fullText,
            data,
            sessionId: sessionId ?? null,
            modelId: modelId || null,
            providerId: providerId || null
          }
        }

        const continuationResponse = await fetchProtectedApi('/api/chat/tool-run/continue', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            runId: toolRunContinuation.runId
          }),
          signal: controller.signal
        })

        // 409 = the run is no longer eligible to resume here (already finished,
        // or the background continuation cron claimed it). That's not an
        // error: the answer is either already saved or will arrive via the
        // realtime subscription. Return what we have and leave the pending
        // turn for the server to complete, instead of surfacing a failure.
        if (continuationResponse.status === 409) {
          const conflictRecovery = await recoverToolRunContinuationClientState({
            data,
            lastChatMessage,
            runId: toolRunContinuation.runId,
            setChatMessages,
            signal: controller.signal
          })

          if (conflictRecovery.handled) {
            data = conflictRecovery.data
          }

          return {
            generatedText: fullText,
            data,
            sessionId: sessionId ?? null,
            modelId: modelId || null,
            providerId: providerId || null
          }
        }

        if (!continuationResponse.ok) {
          throw new Error('Failed to continue tool run')
        }

        return processResponse(
          continuationResponse,
          lastChatMessage,
          true,
          controller,
          setFirstTokenReceived,
          setChatMessages,
          setToolInUse,
          selectedTools,
          setResponseTimeToFirstToken,
          setResponseTimeTotal,
          setResponseTokensTotal,
          startTime,
          onSidekickToolActive,
          {
            seedText: fullText,
            seedData: data,
            depth: continuationDepth + 1
          }
        )
      }

      if (toolRunContinuation) {
        throw new Error('Tool run continuation exceeded the safe retry limit')
      }

      return {
        generatedText: fullText,
        data,
        sessionId: sessionId ?? null,
        modelId: modelId || null,
        providerId: providerId || null,
        finishReason: generationFinishReason
      }
    } catch (error) {
      // A transient connectivity blip (WiFi/VPN handoff → ERR_NETWORK_CHANGED)
      // drops the streaming fetch mid-run. The server-side tool work is
      // already persisted as a chat_tool_run, so reconnect and keep going
      // instead of nuking the turn. Only when we actually know the run id and
      // haven't burned through the small resume budget; otherwise fall through
      // to the normal error handling below (behavior unchanged).
      const resumeRunId = extractResumableToolRunId(data)
      if (
        isTransientNetworkError(error, controller.signal) &&
        resumeRunId &&
        networkResumeAttempts < MAX_TOOL_RUN_NETWORK_RESUME_ATTEMPTS &&
        !controller.signal.aborted
      ) {
        try {
          safeConsole.warn(
            'Stream dropped on a network error; attempting to resume persisted tool run',
            { runId: resumeRunId, attempt: networkResumeAttempts + 1 }
          )
          await new Promise((resolve) =>
            window.setTimeout(resolve, TOOL_RUN_NETWORK_RESUME_DELAY_MS)
          )

          const recovered = await recoverToolRunContinuationClientState({
            data,
            lastChatMessage,
            runId: resumeRunId,
            setChatMessages,
            signal: controller.signal
          })

          if (recovered.handled) {
            data = recovered.data
            return {
              generatedText: fullText,
              data,
              sessionId: sessionId ?? null,
              modelId: modelId || null,
              providerId: providerId || null
            }
          }

          const resumeResponse = await fetchProtectedApi('/api/chat/tool-run/continue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ runId: resumeRunId }),
            signal: controller.signal
          })

          if (resumeResponse.ok) {
            return processResponse(
              resumeResponse,
              lastChatMessage,
              true,
              controller,
              setFirstTokenReceived,
              setChatMessages,
              setToolInUse,
              selectedTools,
              setResponseTimeToFirstToken,
              setResponseTimeTotal,
              setResponseTokensTotal,
              startTime,
              onSidekickToolActive,
              {
                seedText: fullText,
                seedData: data,
                depth: continuationDepth + 1,
                networkResumeAttempts: networkResumeAttempts + 1
              }
            )
          }

          // 409 = already finished or claimed by the background cron — the
          // result is saved or will arrive via realtime. Return what we have
          // without surfacing an error; the pending turn stays for the server
          // to complete.
          if (resumeResponse.status === 409) {
            const conflictRecovery = await recoverToolRunContinuationClientState({
              data,
              lastChatMessage,
              runId: resumeRunId,
              setChatMessages,
              signal: controller.signal
            })

            if (conflictRecovery.handled) {
              data = conflictRecovery.data
            }

            safeConsole.warn('Tool run already being finished server-side after network drop', {
              recovered: conflictRecovery.handled,
              runId: resumeRunId
            })
            return {
              generatedText: fullText,
              data,
              sessionId: sessionId ?? null,
              modelId: modelId || null,
              providerId: providerId || null
            }
          }

          // Not resumable for another reason — fall through to normal handling
          // rather than looping.
          safeConsole.warn('Tool run resume was not accepted; leaving recovery to the server', {
            runId: resumeRunId,
            status: resumeResponse.status
          })
        } catch (resumeError) {
          safeConsole.warn('Tool run resume attempt failed', resumeError)
          // Fall through to normal handling with the ORIGINAL error.
        }
      }

      safeConsole.error('Error processing response:', error)
      const aborted = isAbortLikeError(error, controller.signal)
      const hasStreamedOutput =
        fullText.trim().length > 0 ||
        (continuationState?.seedText?.trim().length ?? 0) > 0 ||
        Boolean(data && typeof data === 'object' && Object.keys(data).length > 0)

      // Mid-stream drop with partial assistant text: keep what we have instead of
      // toasting a hard failure (user can continue / we may auto-continue).
      if (
        !aborted &&
        hasStreamedOutput &&
        isTransientNetworkError(error, controller.signal) &&
        !resumeRunId
      ) {
        safeConsole.warn(
          'Stream dropped after partial assistant output; keeping generated text',
          { length: fullText.length }
        )
        return {
          generatedText: fullText,
          data,
          sessionId: sessionId ?? null,
          modelId: modelId || null,
          providerId: providerId || null,
          finishReason: generationFinishReason ?? 'error'
        }
      }

      if (!aborted) {
        toast.error(t('Something went wrong. Please try again.'))
      }

      // On abort (Stop / force-send), chat-handler already cleaned the pending
      // turn. Removing here races a follow-up send and can delete the NEW
      // pending assistant — which surfaces as another "error processing
      // response" on the force-sent turn.
      if (!aborted && !hasStreamedOutput && !resumeRunId) {
        setChatMessages(removePendingAssistantTurn)
      }
      throw error
    }
  } else {
    throw new Error(t('Response body is null'))
  }
}

export const handleCreateChat = async (
  chatSettings: ChatSettings,
  profile: Tables<'profiles'>,
  selectedWorkspace: Tables<'workspaces'>,
  messageContent: string,
  selectedAssistant: Tables<'assistants'> | null,
  newMessageFiles: ChatFile[],
  selectedTools: Tables<'tools'>[],
  setSelectedChat: React.Dispatch<React.SetStateAction<Tables<'chats'> | null>>,
  setChats: React.Dispatch<React.SetStateAction<Tables<'chats'>[]>>,
  setChatFiles: React.Dispatch<React.SetStateAction<ChatFile[]>>,
  setSelectedTools: React.Dispatch<React.SetStateAction<Tables<'tools'>[]>>,
  setAtomChat?: (newChat: Tables<'chats'>) => void,
  options?: {
    skipGeneratedName?: boolean
    fallbackName?: string
  }
) => {
  const fallbackName =
    options?.fallbackName?.trim() || t('chat_helper.title_generator.default_title')
  let initialChatName = fallbackName

  if (!options?.skipGeneratedName) {
    const generatedName = await generateChatName(messageContent.substring(0, 100))
    const cleanedGeneratedName = generatedName.replace(/"/g, '').trim()
    initialChatName = cleanedGeneratedName || fallbackName
  }

  const storedContext = localStorage.getItem('previousChatContext')
  const context = JSON.parse(storedContext || '{}')

  if (!options?.skipGeneratedName && storedContext) {
    initialChatName = `Continued from "${context.chatName}"`
  }

  const createdChat = await createChat({
    user_id: profile.user_id,
    workspace_id: selectedWorkspace.id,
    assistant_id: selectedAssistant?.id || null,
    context_length: chatSettings.contextLength,
    include_profile_context: chatSettings.includeProfileContext,
    include_workspace_instructions: chatSettings.includeWorkspaceInstructions,
    model: chatSettings.model,
    name: initialChatName,
    prompt: chatSettings.prompt,
    temperature: chatSettings.temperature,
    embeddings_provider: chatSettings.embeddingsProvider,
    last_shared_message_id: null,
    shared_at: null,
    shared_by: null
  })

  const persistedNewMessageFiles = newMessageFiles.filter(isPersistedChatFile)

  if (persistedNewMessageFiles.length > 0) {
    await createChatFiles(
      persistedNewMessageFiles.map((file) => ({
        user_id: profile.user_id,
        chat_id: createdChat.id,
        file_id: file.id
      }))
    )
  }

  // Update both the context state and atom state if available
  setChats((chats) => [{ ...createdChat, metadata: null, type: null }, ...chats])

  // If atom setter is provided, use it to update atom state
  if (setAtomChat) {
    setAtomChat({ ...createdChat, metadata: null, type: null })
  }

  setSelectedTools(selectedTools)
  setSelectedChat({ ...createdChat, metadata: null, type: null })
  setChatFiles((prev) => [...prev, ...persistedNewMessageFiles])

  // finally {
  // Clean up regardless of success or failure
  localStorage.removeItem('previousChatContext')
  localStorage.removeItem('chatGeneratedSummary')
  localStorage.removeItem('chatSummaryApplied')
  sessionStorage.removeItem('currentChatSessionKey')
  // }

  return createdChat
}

/**
 * Migrate public chat conversation to authenticated user's account
 * Creates a new chat with generated name and saves all messages
 */
export const migratePublicChatToAuthenticated = async (
  chatMessages: ChatMessage[],
  chatSettings: ChatSettings,
  profile: Tables<'profiles'>,
  selectedWorkspace: Tables<'workspaces'>,
  selectedAssistant: Tables<'assistants'> | null
): Promise<Tables<'chats'>> => {
  // Find first user message for name generation
  const firstUserMessage = chatMessages.find((msg) => msg.message.role === 'user')
  const messageContent = firstUserMessage?.message.content || 'New Chat'

  // Generate chat name from first user message
  const generatedName = await generateChatName(messageContent.substring(0, 100))
  const cleanedGeneratedName = generatedName.replace(/"/g, '')

  // Create chat record
  let createdChat: Tables<'chats'>
  try {
    createdChat = await createChat({
      user_id: profile.user_id,
      workspace_id: selectedWorkspace.id,
      assistant_id: selectedAssistant?.id || null,
      context_length: chatSettings.contextLength,
      include_profile_context: chatSettings.includeProfileContext,
      include_workspace_instructions: chatSettings.includeWorkspaceInstructions,
      model: chatSettings.model,
      name: cleanedGeneratedName,
      prompt: chatSettings.prompt,
      temperature: chatSettings.temperature,
      embeddings_provider: chatSettings.embeddingsProvider,
      last_shared_message_id: null,
      shared_at: null,
      shared_by: null
    })
  } catch (error) {
    throw error
  }

  // Convert ChatMessage[] to TablesInsert<'messages'>[]
  const filteredMessages = chatMessages.filter(
    (msg) => msg.message.role === 'user' || msg.message.role === 'assistant'
  )
  const messagesToInsert: TablesInsert<'messages'>[] = filteredMessages.map(
    (chatMessage, index) => {
      const msg = chatMessage.message
      return {
        chat_id: createdChat.id,
        user_id: profile.user_id,
        content: msg.content,
        model: msg.model || chatSettings.model,
        role: msg.role,
        sequence_number: index,
        image_paths: msg.image_paths || [],
        assistant_id: msg.role === 'assistant' ? selectedAssistant?.id || null : null,
        annotation: msg.annotation || {},
        session_id: msg.session_id || null,
        model_id: msg.model_id || null,
        provider_id: msg.provider_id || null,
        metadata: msg.metadata || null,
        type: msg.type || null
      }
    }
  )

  // Save all messages to database
  if (messagesToInsert.length > 0) {
    try {
      await createMessages(messagesToInsert)

      // Update chat's updated_at timestamp so it appears in recent chats
      const refreshedChat = await touchChatActivity(createdChat.id)
      Object.assign(createdChat, refreshedChat)
    } catch (error) {
      throw error
    }
  }

  return createdChat
}

const handleRegeneration = async (
  chatMessages: ChatMessage[],
  generatedText: string,
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
): Promise<void> => {
  const lastUserMessageIndex = chatMessages.length - 2
  const lastAssistantMessageIndex = chatMessages.length - 1
  const lastUserMessage = chatMessages[lastUserMessageIndex].message
  const lastAssistantMessage = chatMessages[lastAssistantMessageIndex].message

  // Parse tap-to-reply markers from generated text.
  const { parseSuggestions } = await import('@/lib/utils/parse-suggestions')
  const parsedResponseControls = parseSuggestions(generatedText)
  const cleanedContent = sanitizeAssistantVisibleContent(parsedResponseControls.content)

  const newMetadata = mergeAssistantResponseControlMetadata(
    lastAssistantMessage.metadata as Record<string, unknown> | null,
    parsedResponseControls
  )

  // Update the assistant message with the new generated content
  const sessionId = getAndClearSessionId() || ''
  const modelId = getAndClearModelId() || ''
  const providerId = getAndClearProviderId() || ''
  const updatedAssistantMessage = await updateMessage(lastAssistantMessage.id, {
    content: cleanedContent.trim(),
    session_id: sessionId,
    model_id: modelId,
    provider_id: providerId,
    metadata: newMetadata as Json | null,
    annotation: lastAssistantMessage.annotation,
    type: lastAssistantMessage.type ?? null
  })
  // Update the chat messages state
  const updatedMessages = [...chatMessages]
  updatedMessages[lastAssistantMessageIndex].message = {
    ...updatedAssistantMessage,
    metadata: updatedAssistantMessage.metadata ?? null,
    type: updatedAssistantMessage.type ?? null
  }
  setChatMessages(updatedMessages)
}
const createMessageObject = (
  chatId: string,
  userId: string,
  content: string,
  modelId: string,
  role: string,
  sequenceNumber: number,
  annotation: any = {},
  selectedAssistant: Tables<'assistants'> | null = null,
  session_id: string | null = null,
  model_id: string | null = null,
  provider_id: string | null = null,
  metadata: Json | null = null,
  senderName: string | null = null,
  senderImageUrl: string | null = null,
  imagePaths: string[] = []
): TablesInsert<'messages'> => ({
  chat_id: chatId,
  assistant_id: role === 'assistant' ? selectedAssistant?.id || null : null,
  user_id: userId,
  content,
  model: modelId,
  role,
  sequence_number: sequenceNumber,
  image_paths: imagePaths,
  annotation,
  thumbs_up: null,
  session_id,
  model_id,
  provider_id,
  metadata: resolveMessageMetadata(role, metadata, senderImageUrl),
  sender_name: role === 'user' ? senderName : null,
  type: null
})

const resolveSenderName = (profile: Tables<'profiles'>) => {
  if (typeof profile.display_name === 'string' && profile.display_name.trim()) {
    return profile.display_name.trim()
  }

  if (typeof profile.username === 'string' && profile.username.trim()) {
    return profile.username.trim()
  }

  return null
}

const resolveSenderImageFromMetadata = (metadata: Json | null) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null
  }

  const rawValue = (metadata as Record<string, Json>).senderImageUrl
  return typeof rawValue === 'string' && rawValue.trim() ? rawValue.trim() : null
}

const resolveMessageMetadata = (
  role: string,
  metadata: Json | null,
  senderImageUrl: string | null
) => {
  if (role !== 'user' || !senderImageUrl) {
    return metadata
  }

  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return { senderImageUrl } as Json
  }

  return {
    ...(metadata as Record<string, Json>),
    senderImageUrl
  } as Json
}

const mergeMessageMetadata = (
  baseMetadata: Json | null | undefined,
  nextMetadata: Json | null | undefined
): Json | null => {
  const base =
    baseMetadata && typeof baseMetadata === 'object' && !Array.isArray(baseMetadata)
      ? (baseMetadata as Record<string, Json>)
      : null
  const next =
    nextMetadata && typeof nextMetadata === 'object' && !Array.isArray(nextMetadata)
      ? (nextMetadata as Record<string, Json>)
      : null

  if (!base && !next) {
    return nextMetadata ?? baseMetadata ?? null
  }

  return {
    ...(base ?? {}),
    ...(next ?? {})
  } as Json
}

/**
 * Persist Sidekick user + assistant messages to a chat (for saving Sidekick convos as chats).
 */
export const persistSidekickMessages = async (
  chatId: string,
  profile: Tables<'profiles'>,
  userContent: string,
  generatedText: string,
  chatSettings: ChatSettings,
  assistant: Tables<'assistants'> | null,
  sessionId: string | null,
  modelId: string | null,
  providerId: string | null,
  sequenceStart: number,
  assistantAnnotation: Json | null = null,
  userMetadata: Json | null = null,
  userImagePaths: string[] = []
): Promise<void> => {
  try {
    const parsedResponseControls = parseSuggestions(generatedText)
    const assistantContent = sanitizeAssistantVisibleContent(parsedResponseControls.content)
    const assistantMetadata = mergeAssistantResponseControlMetadata(
      null,
      parsedResponseControls
    ) as Json | null
    const userMsg = createMessageObject(
      chatId,
      profile.user_id,
      userContent,
      chatSettings.model,
      'user',
      sequenceStart,
      {},
      assistant,
      null,
      null,
      null,
      userMetadata,
      resolveSenderName(profile),
      profile.image_url ?? null,
      userImagePaths
    )
    const assistantMsg = createMessageObject(
      chatId,
      profile.user_id,
      assistantContent,
      chatSettings.model,
      'assistant',
      sequenceStart + 1,
      assistantAnnotation ?? {},
      assistant,
      sessionId,
      modelId,
      providerId,
      assistantMetadata,
      null,
      null
    )
    await createMessages([userMsg, assistantMsg])
    await touchChatActivity(chatId)
    await tagChatAsSidekick(chatId)
  } catch (persistErr) {
    throw persistErr
  }
}

export const createPendingAssistantMessage = async ({
  currentChat,
  profile,
  modelData,
  selectedAssistant,
  sequenceNumber,
  startedAt
}: {
  currentChat: Tables<'chats'>
  profile: Tables<'profiles'>
  modelData: LLM
  selectedAssistant: Tables<'assistants'> | null
  sequenceNumber: number
  startedAt: number
}) => {
  const pendingAssistantMessage = createMessageObject(
    currentChat.id,
    profile.user_id,
    '',
    modelData.modelId,
    'assistant',
    sequenceNumber,
    {},
    selectedAssistant,
    null,
    null,
    null,
    buildPendingAssistantTurnMetadata(startedAt),
    null,
    null
  )

  return createMessage(pendingAssistantMessage)
}

export const handleCreateMessages = async (
  chatMessages: ChatMessage[],
  currentChat: Tables<'chats'>,
  profile: Tables<'profiles'>,
  modelData: LLM,
  messageContent: string,
  generatedText: string,
  newMessageImages: MessageImage[],
  isRegeneration: boolean,
  retrievedFileItems: Tables<'file_items'>[],
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setChatFileItems: React.Dispatch<React.SetStateAction<Tables<'file_items'>[]>>,
  setChatImages: React.Dispatch<React.SetStateAction<MessageImage[]>>,
  selectedAssistant: Tables<'assistants'> | null,
  userAnnotations: any = {},
  assistantAnnotations: any = {},
  session_id: string | null = null,
  model_id: string | null = null,
  provider_id: string | null = null,
  metadata: Json | null = null,
  type: string | null = null,
  options: {
    existingUserMessage?: Tables<'messages'> | null
    existingAssistantMessage?: Tables<'messages'> | null
    persistAssistantMessage?: boolean
    onCreatedUserMessage?: (message: Tables<'messages'>) => void | Promise<void>
    userMetadata?: Json | null
    /**
     * Only skip getMessagesByChatId when the caller already persisted this
     * turn's user + pending assistant and priors are known clean.
     */
    skipPersistedMessagesFetch?: boolean
  } = {}
): Promise<Tables<'messages'> | null> => {
  try {
    const existingUserMessage = options.existingUserMessage ?? null
    const existingAssistantMessage = options.existingAssistantMessage ?? null
    const persistAssistantMessage = options.persistAssistantMessage !== false
    const userMetadata = options.userMetadata ?? null

    if (isRegeneration && persistAssistantMessage && generatedText.trim()) {
      // Handle regeneration: update the last two messages
      await handleRegeneration(chatMessages, generatedText, setChatMessages)
      return null
    }
    // ) => {
    //   const notCreatedPriorMessages = chatMessages
    //     .filter(message => message.message.id === "")
    //     .map(reconstructContentWithCodeBlocksInChatMessage)

    //   const finalUserMessage: TablesInsert<"messages"> = {
    //     chat_id: currentChat.id,
    //     assistant_id: null,
    //     user_id: profile.user_id,
    //     content: messageContent, // This doesn't need reconstruction as it's the original user input
    //     model: modelData.modelId,
    //     role: "user",
    //     sequence_number: chatMessages.length,
    //     image_paths: [],
    //     annotation: userAnnotations
    //   }

    // Happy path (tools-stream): caller opts in after persisting user + pending
    // assistant. Never skip solely because both ids happen to be present.
    const canSkipPersistedMessagesFetch = Boolean(
      options.skipPersistedMessagesFetch &&
        existingUserMessage?.id &&
        existingAssistantMessage?.id
    )

    const persistedMessages = canSkipPersistedMessagesFetch
      ? ([] as Tables<'messages'>[])
      : await getMessagesByChatId(currentChat.id)

    const unsavedPriorMessages = canSkipPersistedMessagesFetch
      ? []
      : filterUnpersistedChatMessages(chatMessages, persistedMessages).filter(
          (chatMessage) => {
            if (chatMessage.message.role === 'user') {
              return true
            }

            return (
              chatMessage.message.role === 'assistant' &&
              chatMessage.message.content.trim().length > 0
            )
          }
        )
    const unsavedPriorMessageSlots = new Set(
      unsavedPriorMessages.map((chatMessage) =>
        getMessagePersistenceSlot(chatMessage.message)
      )
    )

    // Persist earlier optimistic turns before saving the current one.
    const notCreatedPriorMessages = unsavedPriorMessages.map(
      reconstructContentWithCodeBlocksInChatMessage
    )

    // Ensure proper sequence number by getting the max sequence_number from sorted messages
    const sortedMessages = [...chatMessages].sort(
      (a, b) => a.message.sequence_number - b.message.sequence_number
    )
    const localMaxSequenceNumber =
      sortedMessages.length > 0
        ? Math.max(...sortedMessages.map((msg) => msg.message.sequence_number))
        : -1
    const persistedMaxSequenceNumber =
      persistedMessages.length > 0
        ? Math.max(...persistedMessages.map((msg) => msg.sequence_number))
        : -1
    const nextSequenceNumber =
      Math.max(localMaxSequenceNumber, persistedMaxSequenceNumber) + 1
    const currentUserSequenceNumber =
      existingUserMessage?.sequence_number ?? nextSequenceNumber

    const preparedMessageImages = existingUserMessage
      ? { imagePaths: [], images: [] }
      : await prepareChatMessageImages({
          images: newMessageImages,
          userId: profile.user_id,
          chatId: currentChat.id,
          uploadLimitContext: getMessageImageUploadLimitContext(currentChat)
        })

    // Prepare new user and assistant messages
    const newUserMessage = createMessageObject(
      currentChat.id,
      profile.user_id,
      messageContent,
      modelData.modelId,
      'user',
      currentUserSequenceNumber,
      userAnnotations,
      selectedAssistant,
      null,
      null,
      null,
      userMetadata,
      resolveSenderName(profile),
      profile.image_url ?? null,
      preparedMessageImages.imagePaths
    )

    let newAssistantMessage: TablesInsert<'messages'> | null = null
    let updatedExistingAssistantMessage: Tables<'messages'> | null = null
    let replacedExistingAssistantMessageId: string | null = null

    if (persistAssistantMessage) {
      // Parse suggestions from generated text before reconstructing content
      const { parseSuggestions } = await import('@/lib/utils/parse-suggestions')
      const parsedResponseControls = parseSuggestions(generatedText)
      const cleanedContent = sanitizeAssistantVisibleContent(parsedResponseControls.content)

      const assistantContent = reconstructContentWithCodeBlocks(
        cleanedContent,
        chatMessages[chatMessages.length - 1]?.codeBlocks || []
      )
      // Update the assistant message with the new generated content
      const sessionId = getAndClearSessionId()
      const modelId = getAndClearModelId()
      const providerId = getAndClearProviderId()

      const assistantMetadata = completePendingAssistantTurnMetadata(
        mergeMessageMetadata(
          existingAssistantMessage?.metadata ?? null,
          mergeMessageMetadata(
            metadata || null,
            mergeAssistantResponseControlMetadata(null, parsedResponseControls) as Json | null
          )
        )
      )
      const createAssistantMessageInsert = () =>
        createMessageObject(
          currentChat.id,
          profile.user_id,
          assistantContent,
          modelData.modelId,
          'assistant',
          currentUserSequenceNumber + 1,
          assistantAnnotations,
          selectedAssistant,
          (session_id = sessionId),
          (model_id = modelId),
          (provider_id = providerId),
          assistantMetadata,
          null,
          null
        )

      if (existingAssistantMessage) {
        try {
          updatedExistingAssistantMessage = await updateMessage(existingAssistantMessage.id, {
            content: assistantContent,
            session_id: sessionId,
            model_id: modelId,
            provider_id: providerId,
            annotation: assistantAnnotations,
            metadata: assistantMetadata,
            type: type ?? existingAssistantMessage.type ?? null
          })
        } catch (error) {
          safeConsole.warn(
            'Failed to update pending assistant message; creating a replacement',
            error
          )
          replacedExistingAssistantMessageId = existingAssistantMessage.id
          newAssistantMessage = createAssistantMessageInsert()
          // Drop the orphaned pending row so reload does not show two assistants.
          try {
            await deleteMessage(existingAssistantMessage.id)
          } catch (deleteError) {
            safeConsole.warn(
              'Failed to delete orphaned pending assistant after replacement',
              deleteError
            )
          }
        }
      } else {
        newAssistantMessage = createAssistantMessageInsert()
      }
    }

    const existingAssistantSlot = existingAssistantMessage
      ? getMessagePersistenceSlot(existingAssistantMessage)
      : null
    const newAssistantSlot = newAssistantMessage
      ? `${newAssistantMessage.sequence_number}:assistant`
      : null

    // Combine all messages that need to be processed
    const allMessagesToProcess = [
      ...notCreatedPriorMessages
        .filter((msg) => {
          if (msg.message.role !== 'assistant') return true
          const slot = getMessagePersistenceSlot(msg.message)
          // Never insert a twin of the assistant we are updating/creating for
          // this turn — that persisted as two full Message components on reload.
          if (existingAssistantSlot && slot === existingAssistantSlot) return false
          if (newAssistantSlot && slot === newAssistantSlot) return false
          if (
            existingAssistantMessage &&
            msg.message.id &&
            msg.message.id === existingAssistantMessage.id
          ) {
            return false
          }
          return true
        })
        .map((msg) =>
        createMessageObject(
          currentChat.id,
          profile.user_id,
          msg.message.content,
          modelData.modelId,
          msg.message.role,
          msg.message.sequence_number,
          msg.message.annotation,
          selectedAssistant,
          null,
          null,
          null,
          msg.message.metadata || null,
          msg.message.role === 'user' ? (msg.message.sender_name ?? resolveSenderName(profile)) : null,
          msg.message.role === 'user'
            ? (resolveSenderImageFromMetadata(msg.message.metadata) ?? profile.image_url ?? null)
            : null,
          Array.isArray(msg.message.image_paths)
            ? msg.message.image_paths.filter(Boolean)
            : []
        )
      ),
      ...(existingUserMessage ? [] : [newUserMessage]),
      ...(newAssistantMessage ? [newAssistantMessage] : [])
    ]

    // Persist all messages to the database
    const createdMessages = await createMessages(allMessagesToProcess)

    // Update chat's updated_at timestamp to mark it as recently active
    try {
      await touchChatActivity(currentChat.id)
    } catch (error) {
      safeConsole.warn('Failed to refresh chat updated_at after persisting messages', error)
    }

    // Extract the persisted user and assistant messages
    const createdPriorMessageCount = notCreatedPriorMessages.length
    const createdUserMessageIndex = existingUserMessage
      ? -1
      : createdPriorMessageCount
    const createdUserMessage =
      existingUserMessage ?? createdMessages[createdUserMessageIndex]
    const createdAssistantMessage = persistAssistantMessage
      ? updatedExistingAssistantMessage ?? createdMessages[createdMessages.length - 1]
      : null

    if (!createdUserMessage) {
      throw new Error('Failed to persist user message')
    }

    if (!existingUserMessage && options.onCreatedUserMessage) {
      void Promise.resolve(options.onCreatedUserMessage(createdUserMessage)).catch((error) => {
        safeConsole.warn('Failed to run created user message callback:', error)
      })
    }

    // TODO console.log(
    //   "paths",
    //   paths,
    //   newMessageImages,
    //   createdUserMessage,
    //   createdUserMessage.id
    // )

    // Update chat images state
    if (!existingUserMessage && preparedMessageImages.images.length > 0) {
      setChatImages((prevImages) => [
        ...prevImages,
        ...preparedMessageImages.images.map((obj) => ({
          ...obj,
          messageId: createdUserMessage.id
        }))
      ])
    }

    // Create message file items for the assistant message
    if (createdAssistantMessage && retrievedFileItems.length > 0) {
      await createMessageFileItems(
        retrievedFileItems.map((fileItem) => ({
          user_id: profile.user_id,
          message_id: createdAssistantMessage.id,
          file_item_id: fileItem.id
        }))
      )
    }

    const filteredChatMessages = chatMessages.filter(
      (chatMessage) =>
        chatMessage.message.id !== replacedExistingAssistantMessageId &&
        !unsavedPriorMessageSlots.has(
          getMessagePersistenceSlot(chatMessage.message)
        )
    )

    // Replace existing rows in place when finalize is handed a live array that
    // already contains them — appending would twin the Message components.
    const upsertByIdOrSlot = (
      list: ChatMessage[],
      next: ChatMessage
    ): ChatMessage[] => {
      const nextId = next.message.id?.trim()
      const nextSlot = getMessagePersistenceSlot(next.message)
      const index = list.findIndex((chatMessage) => {
        const id = chatMessage.message.id?.trim()
        if (nextId && id && id === nextId) return true
        return getMessagePersistenceSlot(chatMessage.message) === nextSlot
      })
      if (index < 0) return [...list, next]
      const copy = [...list]
      copy[index] = next
      return copy
    }

    let finalChatMessages: ChatMessage[] = [...filteredChatMessages]

    if (existingUserMessage) {
      finalChatMessages = upsertByIdOrSlot(finalChatMessages, {
        message: createdUserMessage,
        fileItems: []
      })
    }

    if (updatedExistingAssistantMessage) {
      finalChatMessages = upsertByIdOrSlot(finalChatMessages, {
        message: updatedExistingAssistantMessage,
        fileItems: retrievedFileItems.map((item) => item.id)
      })
    }

    for (const msg of createdMessages) {
      finalChatMessages = upsertByIdOrSlot(finalChatMessages, {
        message: msg,
        fileItems:
          createdAssistantMessage && msg.id === createdAssistantMessage.id
            ? retrievedFileItems.map((item) => item.id)
            : []
      })
    }

    finalChatMessages = finalChatMessages.sort(
      (a, b) => a.message.sequence_number - b.message.sequence_number
    )
    const normalizedFinalChatMessages: ChatMessage[] = finalChatMessages.map(
      (chatMessage): ChatMessage => ({
        ...chatMessage,
        message: {
          ...chatMessage.message,
          metadata: (chatMessage.message.metadata ?? null) as any,
          type: chatMessage.message.type ?? null
        }
      })
    )

    const parsedFinalChatMessages = normalizedFinalChatMessages.map((chatMessage) =>
      parseChatMessageCodeBlocksAndContent(chatMessage)
    )
    setChatFileItems((prevItems) => [
      ...prevItems,
      ...retrievedFileItems.filter((item) => !prevItems.some((prev) => prev.id === item.id))
    ])
    setChatMessages((currentMessages) =>
      mergePersistedChatMessages(parsedFinalChatMessages, currentMessages)
    )
    return createdUserMessage
  } catch (error) {
    safeConsole.error('Error in handleCreateMessages:', error)
    throw error
    // Optionally, you could add user-facing error handling here, such as displaying a toast notification
  }
}
